import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { DiagnosticsPanel } from '../common/DiagnosticsPanel';
import Hls from 'hls.js';
import mpegts from 'mpegts.js';
import {
  enableScreenWakeLock,
  disableScreenWakeLock,
  getScreenWakeLockStatus,
  getDetailedWakeLockStatus,
} from '../../utils/screenWakeLock';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize2,
  Minimize2,
  ChevronLeft,
  ChevronRight,
  Shield,
  RefreshCw,
  X,
  Layers,
  Calendar,
  Cast,
  Lock,
  Unlock,
  SquareDashedBottom,
  Clock,
  Activity,
  Copy,
  Check,
} from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';
import { ChannelStreamProfile, apiClient } from '../../lib/apiClient';
import { getProxiedStreamUrl, convertToHlsUrl } from '../../utils/streamProxy';
import { remoteLog } from '../../utils/remoteLog';
import { withServerHost, withListAccount } from '../../utils/managerDns';
import { getSafeImageUrl } from '../../utils/imageUtils';
import { CastModal } from './CastModal';
import { TvLoadingOverlay } from './TvLoadingOverlay';
import { toggleVideoFullscreen, isVideoInFullscreen } from '../../utils/fullscreen';
import { getActiveAndUpcomingPrograms, getCurrentProgram } from '../../utils/epgService';
import { ChannelResolution, ChannelQualityVariant } from '../../types/iptv';
import { extractChannelQualityInfo, RESOLUTION_HIERARCHY } from '../../utils/channelQualityUtils';
import { useScreenLockTimeout } from '../../hooks/useScreenLockTimeout';

const FAST_LIVE_HLS_CONFIG = {
  enableWorker: true,
  // Desativado: streams IPTV não são LL-HLS. Com lowLatency=true, solicita fragmentos na ponta extrema, esgotando buffer a cada 15-20s
  lowLatencyMode: false,
  startLevel: -1,
  initialLiveManifestSize: 2,
  liveSyncDurationCount: 3,
  liveMaxLatencyDurationCount: 8,
  startFragPrefetch: false,
  abrEwmaDefaultEstimate: 5000000,
  progressive: true,
  manifestLoadingTimeOut: 10000,
  manifestLoadingMaxRetry: 4,
  manifestLoadingRetryDelay: 800,
  fragLoadingTimeOut: 12000,
  fragLoadingMaxRetry: 4,
  fragLoadingRetryDelay: 800,
  backBufferLength: 10,
  maxBufferLength: 20,
  maxMaxBufferLength: 40,
  maxBufferSize: 50 * 1024 * 1024,
  nudgeOffset: 0.2,
  nudgeMaxRetry: 5,
  maxBufferHole: 0.5,
  highBufferWatchdogPeriod: 3,
};

// V9.0.4 — Rota do ao vivo no Web: iPhone, iPad e Mac SEMPRE pela Oracle (regra do Mario e do AGENTS.md,
// travamentos do AVPlayer). Os outros aparelhos tentam DIRETO no servidor da lista primeiro e só usam a Oracle se o direto
// não abrir.
// V9.2.0 (pedido do Mario, 03/11): SEM memória de falhas. Todo canal tenta SEMPRE direto na Fusion primeiro; se não
// abrir (erro ou 8 s sem imagem), recarrega AQUELE canal pela Oracle, só desta vez. Trocou de canal ou voltou depois:
// tenta direto de novo. Antes, 2 falhas (ex.: LEBISCA-01/02 em 03/11, ou uma instabilidade) deixavam TODOS os canais
// presos na Oracle por 24 h.
const LIVE_DIRECT_TIMEOUT_MS = 8000;
function hostOf(u: string): string {
  try {
    return new URL(u).host.toLowerCase();
  } catch {
    return '';
  }
}

const MSE_FALLBACK_CHANNELS_KEY = 'iptv_channels_need_mse_fallback';

function getMseFallbackChannels(): Set<string> {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(MSE_FALLBACK_CHANNELS_KEY) : null;
    if (raw) return new Set(JSON.parse(raw));
  } catch {}
  return new Set();
}

function markChannelAsMseFallback(channelId: string) {
  try {
    if (typeof localStorage === 'undefined') return;
    const set = getMseFallbackChannels();
    if (!set.has(channelId)) {
      set.add(channelId);
      localStorage.setItem(MSE_FALLBACK_CHANNELS_KEY, JSON.stringify(Array.from(set)));
    }
  } catch {}
}

function channelNeedsMseFallback(channelId?: string, channelProfile?: ChannelStreamProfile): boolean {
  if (!channelId) return false;
  // 1. Prioridade do Banco de Dados Central MySQL (Inteligência Coletiva):
  if (channelProfile) {
    if (channelProfile.preferred_player === 'hls_mse' || channelProfile.preferred_player === 'mpegts') {
      return true;
    }
    if (channelProfile.preferred_player === 'native') {
      return false;
    }
  }
  // 2. Cache Local / Fallback persistente
  return getMseFallbackChannels().has(channelId);
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// V8.2.6: MOTIVO REAL DA FALHA. Antes toda falha mostrava "formato de vídeo incompatível". Agora,
// quando o canal não abre, o app consulta o mesmo endereço do vídeo e mostra o motivo verdadeiro
// (sem internet / servidor não responde / acesso negado (país/IP) / canal fora do ar / limite de
// conexões / resposta inválida / vídeo realmente incompatível) + o detalhe técnico.
// ─────────────────────────────────────────────────────────────────────────────────────────────
type StreamProbe = { kind: 'http' | 'network' | 'timeout' | 'no_url'; status?: number; contentType?: string; snippet?: string };

async function probeStreamUrl(url: string): Promise<StreamProbe> {
  if (!url) return { kind: 'no_url' };
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    try { ctrl?.abort(); } catch {}
  }, 8000);
  try {
    const res = await fetch(url, { cache: 'no-store', credentials: 'omit', signal: ctrl?.signal });
    const contentType = (res.headers.get('content-type') || '').toLowerCase();
    let snippet = '';
    // Lê só o começo da resposta (canal .ts é contínuo: nunca lê tudo)
    try {
      const reader = res.body && (res.body as any).getReader ? (res.body as any).getReader() : null;
      if (reader) {
        const chunks: Uint8Array[] = [];
        let total = 0;
        while (total < 2048) {
          const { done, value } = await reader.read();
          if (done || !value) break;
          chunks.push(value);
          total += value.length;
        }
        try { reader.cancel(); } catch {}
        const buf = new Uint8Array(Math.min(total, 2048));
        let off = 0;
        for (const c of chunks) {
          const take = Math.min(c.length, buf.length - off);
          if (take <= 0) break;
          buf.set(c.subarray(0, take), off);
          off += take;
        }
        snippet = new TextDecoder('utf-8', { fatal: false }).decode(buf);
      } else if (!/mp2t|octet-stream|video\//.test(contentType)) {
        snippet = (await res.text()).slice(0, 2048);
      }
    } catch {}
    try { ctrl?.abort(); } catch {}
    return { kind: 'http', status: res.status, contentType, snippet };
  } catch {
    return { kind: timedOut ? 'timeout' : 'network' };
  } finally {
    clearTimeout(timer);
  }
}

// V8.3.3: confere o 1º PEDAÇO de vídeo da lista (a lista pode chegar normal e os pedaços serem recusados —
// ex.: canais +18 protegidos por Cloudflare). Segue 1 nível de sub-lista (lista de qualidades).
async function probeFirstSegment(playlistUrl: string, playlistText: string, depth = 0): Promise<{ url: string; probe: StreamProbe } | null> {
  const lines = playlistText.split(/\r?\n/).map((l) => l.trim()).filter((l) => l && !l.startsWith('#'));
  if (lines.length === 0) return null;
  let next = lines[0];
  try {
    next = new URL(next, playlistUrl).href;
  } catch {
    return null;
  }
  const p = await probeStreamUrl(next);
  if (depth === 0 && p.kind === 'http' && (p.status || 0) < 300 && (p.snippet || '').includes('#EXTM3U')) {
    return probeFirstSegment(next, p.snippet || '', 1);
  }
  return { url: next, probe: p };
}

function cleanSnippet(s?: string): string {
  if (!s) return '';
  const txt = s.replace(/<[^>]*>/g, ' ').replace(/[^\x20-\x7EÀ-ÿ]+/g, ' ').replace(/\s+/g, ' ').trim();
  return txt.length > 140 ? txt.slice(0, 140) + '…' : txt;
}

function explainStreamFailure(p: StreamProbe, engineDetail: string): { title: string; message: string; detail: string } {
  const eng = engineDetail ? ` · ${engineDetail}` : '';
  if (p.kind === 'no_url') {
    return { title: 'Canal sem endereço', message: 'Este canal não tem endereço de vídeo cadastrado na lista.', detail: `Sem URL${eng}` };
  }
  if (p.kind === 'network') {
    return {
      title: 'Sem conexão com o servidor de vídeo',
      message:
        'O aparelho não conseguiu se conectar ao servidor de vídeo (stream.mariozeli.com.br). Verifique a internet. Redes de hotel, empresa ou de alguns países podem bloquear esse endereço.',
      detail: `Falha de rede (sem resposta)${eng}`,
    };
  }
  if (p.kind === 'timeout') {
    return {
      title: 'Servidor de vídeo não respondeu',
      message: 'O servidor demorou mais de 8 segundos para responder. Internet lenta ou servidor sobrecarregado — tente novamente em instantes.',
      detail: `Tempo esgotado (8 s)${eng}`,
    };
  }
  const st = p.status || 0;
  const txt = cleanSnippet(p.snippet);
  const body = txt ? ` · resposta: "${txt}"` : '';
  if (st === 401 || st === 403 || st === 451) {
    return {
      title: 'Acesso negado pelo provedor',
      message:
        'O provedor recusou este acesso. Isso costuma acontecer com acesso de FORA DO BRASIL (bloqueio por país/IP) ou com conta sem permissão para este canal.',
      detail: `HTTP ${st}${body}${eng}`,
    };
  }
  if (st === 404 || st === 410) {
    return {
      title: 'Canal fora do ar',
      message: 'O provedor não encontrou este canal. Ele pode ter saído do ar ou mudado de endereço — tente outro canal.',
      detail: `HTTP ${st}${body}${eng}`,
    };
  }
  if (st === 429 || st === 456 || st === 458 || st === 509) {
    return {
      title: 'Limite de conexões atingido',
      message: 'A conta do provedor atingiu o limite de telas/conexões ao mesmo tempo. Feche o canal em outro aparelho e tente de novo.',
      detail: `HTTP ${st}${body}${eng}`,
    };
  }
  if (st >= 500) {
    return {
      title: 'Servidor com falha',
      message: 'O servidor do provedor (ou o nosso servidor de vídeo) respondeu com erro. Normalmente é temporário — tente novamente em instantes.',
      detail: `HTTP ${st}${body}${eng}`,
    };
  }
  if (st >= 200 && st < 300) {
    const isVideo =
      /mpegurl|mp2t|octet-stream|video\//.test(p.contentType || '') ||
      (p.snippet || '').includes('#EXTM3U') ||
      (p.snippet || '').charCodeAt(0) === 0x47; // MPEG-TS começa com 0x47
    if (isVideo) {
      return {
        title: 'Vídeo incompatível com este aparelho',
        message:
          'O sinal está chegando normalmente, mas este aparelho não conseguiu decodificar o vídeo deste canal (codec/formato). Tente em outro aparelho ou navegador.',
        detail: `HTTP ${st} · ${p.contentType || 'sem tipo'}${eng}`,
      };
    }
    return {
      title: 'Resposta inválida do provedor',
      message: 'O provedor respondeu, mas não enviou vídeo. A conta pode estar vencida/bloqueada ou o canal está com problema.',
      detail: `HTTP ${st} · ${p.contentType || 'sem tipo'}${body}${eng}`,
    };
  }
  return {
    title: 'Falha ao abrir o canal',
    message: 'O servidor respondeu com um código inesperado ao tentar abrir este canal.',
    detail: `HTTP ${st}${body}${eng}`,
  };
}


export const LivePlayer: React.FC = () => {
  const {
    currentChannel,
    channels,
    stopPlayback,
    zapChannel,
    showToast,
    epgData,
    loadChannelEPG,
    isEpgLoading,
    isPlayerControlsVisible,
    setIsPlayerControlsVisible,
    pokePlayerControls,
    hidePlayerControlsNow,
    setControlsAutoHideSuppressed,
    getStreamServer,
    getStreamAccount,
    settings,
    switchChannelQuality,
    setCurrentStreamRoute,
    deviceId,
    channelProfiles,
    getChannelStreamProfile,
    saveChannelStreamProfile,
    isAdmin,
    authUser,
    isCasting,
  } = useIPTV();
  // V8.5.1: botão de diagnóstico/telemetria só para a conta do desenvolvedor (MARIOZELI, admin)
  const canSeeTelemetry = !!isAdmin && String((authUser as any)?.username || '').trim().toLowerCase() === 'mariozeli';

  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const mpegtsRef = useRef<any>(null);

  // Cast: enquanto a TV toca, o celular PARA de baixar o vídeo (só pausar deixava a conexão aberta e o
  // celular e a TV usavam a mesma conta do pool ao mesmo tempo). Ao parar o Cast, volta a carregar.
  const wasCastingRef = useRef(false);
  useEffect(() => {
    const video = videoRef.current;
    if (isCasting) {
      try { hlsRef.current?.stopLoad(); } catch {}
      try { mpegtsRef.current?.unload?.(); } catch {}
      try { video?.pause(); } catch {}
      // V8.8.6: ao parar o Cast o canal é aberto de novo (pelo efeito de carregar o canal)
      currentLoadedStreamUrlRef.current = '';
    } else if (wasCastingRef.current) {
      try { hlsRef.current?.startLoad(); } catch {}
      try { mpegtsRef.current?.load?.(); } catch {}
    }
    wasCastingRef.current = isCasting;
  }, [isCasting]);
  const rawFallbackStreamUrlRef = useRef<string>('');
  const hasTriedNoVideoFallbackRef = useRef<boolean>(false);
  const noVideoWarningShownRef = useRef<boolean>(false);
  const hasRenderedVideoFrameRef = useRef<boolean>(false);
  const lastDecodedFrameBaseRef = useRef<number>(0);

  const epgDrawerRef = useRef<HTMLDivElement>(null);

  // Padlock & Touch Lock state
  const [isTouchLocked, setIsTouchLocked] = useState(false);
  const [isUnlockUIVisible, setIsUnlockUIVisible] = useState(false);
  const [unlockTapCount, setUnlockTapCount] = useState(0);
  const [lockedBannerText, setLockedBannerText] = useState<string | null>(null);
  const unlockUIRef = useRef<NodeJS.Timeout | null>(null);
  const bannerTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastUnlockClickRef = useRef<number>(0);
  const tapResetTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const isMutedRef = useRef(false);
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isCastModalOpen, setIsCastModalOpen] = useState(false);
  const [isPipActive, setIsPipActive] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [bufferingSeconds, setBufferingSeconds] = useState<number>(0);
  const [networkRetryCount, setNetworkRetryCount] = useState<number>(0);
  const [lastNetworkError, setLastNetworkError] = useState<string>('');
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // V8.2.6: título e detalhe técnico do motivo REAL da falha (ver explainStreamFailure)
  const [errorTitle, setErrorTitle] = useState('');
  const [errorDetail, setErrorDetail] = useState('');
  const failureTokenRef = useRef(0);
  const failureReportedRef = useRef(false);
  const reportFailureRef = useRef<(engineDetail: string, probeUrl?: string) => void>(() => {});
  const [showEpgDrawer, setShowEpgDrawer] = useState(false);
  const [activeResolution, setActiveResolution] = useState('HD');
  const [decodedResolution, setDecodedResolution] = useState<string | null>(null);
  const [aspectRatio, setAspectRatio] = useState<'contain' | 'cover' | 'fill'>('contain');
  const [isManualReconnecting, setIsManualReconnecting] = useState(false);
  // V8.3.3: motivo REAL quando o canal fica carregando muito tempo (antes: "Canal sem sinal no servidor" fixo)
  const [stallInfo, setStallInfo] = useState<{ title: string; detail: string } | null>(null);

  // Contador de tempo em buffering para diagnóstico em tempo real
  useEffect(() => {
    if (!isBuffering || hasError) {
      setBufferingSeconds(0);
      return;
    }
    const timer = setInterval(() => {
      setBufferingSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [isBuffering, hasError, currentChannel?.id]);

  // Mensagens dinâmicas de diagnóstico para explicar o status do canal
  const { loadingStatus, loadingSubtitle } = useMemo(() => {
    if (networkRetryCount > 2) {
      return {
        loadingStatus: 'INSTABILIDADE NO SERVIDOR IPTV',
        loadingSubtitle: 'O servidor IPTV da lista não está transmitindo este canal no momento. Troque de canal ou tente reconectar.',
      };
    }
    if (bufferingSeconds >= 16 && stallInfo) {
      return { loadingStatus: stallInfo.title, loadingSubtitle: stallInfo.detail };
    }
    if (bufferingSeconds >= 16) {
      return {
        loadingStatus: 'CANAL SEM SINAL NO SERVIDOR',
        loadingSubtitle: 'O servidor IPTV da lista não enviou pacotes de transmissão. O player está pronto. Troque de canal pelos botões.',
      };
    }
    if (bufferingSeconds >= 8) {
      return {
        loadingStatus: 'SERVIDOR IPTV DEMORANDO...',
        loadingSubtitle: 'Aguardando fluxo de vídeo da operadora. Player ativo e pronto.',
      };
    }
    if (bufferingSeconds >= 4) {
      return {
        loadingStatus: 'AGUARDANDO SINAL...',
        loadingSubtitle: 'Conectando ao canal e aguardando dados...',
      };
    }
    return {
      loadingStatus: 'SINTONIZANDO...',
      loadingSubtitle: 'Carregando transmissão ao vivo...',
    };
  }, [bufferingSeconds, networkRetryCount, stallInfo]);

  // Encerramento automático de sessão após 60s com dispositivo bloqueado ou em background
  useScreenLockTimeout({
    enabled: !!currentChannel,
    isPipActive,
    timeoutSeconds: 60,
    onTimeout: stopPlayback,
  });

  // Audio tracks
  const [audioTracks, setAudioTracks] = useState<Array<{ id: number; name: string; lang: string }>>([]);
  const [currentAudioTrack, setCurrentAudioTrack] = useState<number>(-1);
  const [showAudioMenu, setShowAudioMenu] = useState(false);

  // EPG programs for active channel (lookup by channel id or epgId)
  const rawChannelEpgPrograms = useMemo(() => {
    if (!currentChannel || !epgData) return [];
    return (
      epgData[currentChannel.id] ||
      (currentChannel.epgId ? epgData[currentChannel.epgId] : []) ||
      []
    );
  }, [currentChannel, epgData]);

  // Programas estritamente ativos (no ar agora) e próximos nas próximas 24 horas
  const channelEpgPrograms = useMemo(() => {
    return getActiveAndUpcomingPrograms(rawChannelEpgPrograms);
  }, [rawChannelEpgPrograms]);

  // Current Live EPG Program (programa atualmente transmitido no ar)
  const currentLiveProgram = useMemo(() => {
    return getCurrentProgram(rawChannelEpgPrograms);
  }, [rawChannelEpgPrograms]);

  const lastEpgFetchChannelIdRef = useRef<string | null>(null);



  // Auto-fetch EPG for active channel if not loaded yet (apenas 1 vez por troca de canal)
  useEffect(() => {
    if (currentChannel && lastEpgFetchChannelIdRef.current !== currentChannel.id) {
      lastEpgFetchChannelIdRef.current = currentChannel.id;
      if (!rawChannelEpgPrograms || rawChannelEpgPrograms.length === 0) {
        loadChannelEPG(currentChannel);
      }
    }
  }, [currentChannel?.id, rawChannelEpgPrograms.length, loadChannelEPG]);

  // Current Program Elapsed Progress Percentage
  const currentProgramProgress = useMemo(() => {
    if (!currentLiveProgram) return 0;
    const now = Date.now();
    const start = new Date(currentLiveProgram.startTime).getTime();
    const end = new Date(currentLiveProgram.endTime).getTime();
    if (isNaN(start) || isNaN(end) || end <= start) return 0;
    if (now <= start) return 0;
    if (now >= end) return 100;
    return Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
  }, [currentLiveProgram]);

  // Lista de qualidades de transmissão disponíveis para o canal ativo (SD, HD, FHD, 4K)
  const availableQualities = useMemo(() => {
    if (!currentChannel) return [];

    // 1. Se o canal já tem qualidades consolidadas com mais de 1 opção
    if (currentChannel.qualities && currentChannel.qualities.length > 1) {
      return currentChannel.qualities;
    }

    // 2. Busca dinâmica na lista geral de canais (channels) se há variantes com mesmo nome canônico
    const cleanCurName = extractChannelQualityInfo(currentChannel.name).baseName.toLowerCase().trim();
    const cleanCurKey = cleanCurName.replace(/[^a-z0-9]/g, '');

    const matchingInGrid = channels.find((c) => {
      const cKey = extractChannelQualityInfo(c.name).baseName.toLowerCase().replace(/[^a-z0-9]/g, '');
      return cKey === cleanCurKey && c.qualities && c.qualities.length > 1;
    });

    if (matchingInGrid?.qualities && matchingInGrid.qualities.length > 0) {
      return matchingInGrid.qualities;
    }

    // 3. Procura por todos os canais individuais na grade que compartilham o mesmo baseName
    const matchingChannels = channels.filter((c) => {
      const cKey = extractChannelQualityInfo(c.name).baseName.toLowerCase().replace(/[^a-z0-9]/g, '');
      return cKey === cleanCurKey;
    });

    if (matchingChannels.length > 1) {
      const variantsMap = new Map<ChannelResolution, ChannelQualityVariant>();
      for (const ch of matchingChannels) {
        if (ch.qualities && ch.qualities.length > 0) {
          for (const q of ch.qualities) {
            if (!variantsMap.has(q.resolution)) variantsMap.set(q.resolution, q);
          }
        } else {
          const { detectedResolution } = extractChannelQualityInfo(ch.name, ch.groupTitle);
          const res = detectedResolution || ch.resolution || 'SD';
          if (!variantsMap.has(res)) {
            variantsMap.set(res, { resolution: res, streamUrl: ch.streamUrl, originalName: ch.name });
          }
        }
      }
      if (variantsMap.size > 0) {
        return Array.from(variantsMap.values()).sort(
          (a, b) => (RESOLUTION_HIERARCHY[a.resolution] || 0) - (RESOLUTION_HIERARCHY[b.resolution] || 0)
        );
      }
    }

    if (currentChannel.qualities && currentChannel.qualities.length > 0) {
      return currentChannel.qualities;
    }

    const singleRes = (currentChannel.resolution || activeResolution || 'SD') as ChannelResolution;
    return [{ resolution: singleRes, streamUrl: currentChannel.streamUrl, originalName: currentChannel.name }];
  }, [currentChannel, channels, activeResolution]);


  // V9.0.4: rota do canal atual. iPhone/iPad/Mac (qualquer navegador) = sempre Oracle. Outros = direto, a não
  // ser que este canal já tenha caído para a Oracle agora (oracleChannelId) ou o direto esteja bloqueado neste
  // aparelho para o servidor.
  const isAppleUA = useMemo(
    () =>
      typeof navigator !== 'undefined' &&
      (/iPad|iPhone|iPod|Macintosh|Mac OS X/.test(navigator.userAgent) || /^Mac/.test(navigator.platform || '')),
    []
  );
  const [oracleChannelId, setOracleChannelId] = useState<string | null>(null);
  const liveServerHost = useMemo(
    () => (currentChannel ? hostOf(withListAccount(withServerHost(currentChannel.streamUrl, getStreamServer(currentChannel.playlistId)), getStreamAccount(currentChannel.playlistId))) : ''),
    [currentChannel, getStreamServer, getStreamAccount]
  );
  const liveRoute: 'direct' | 'oracle' = useMemo(() => {
    if (!currentChannel || isAppleUA) return 'oracle';
    if (oracleChannelId === currentChannel.id) return 'oracle';
    return 'direct';
  }, [currentChannel, isAppleUA, oracleChannelId]);
  // V9.2.0: trocou de canal → a reserva pela Oracle do canal anterior não vale mais (voltando nele, tenta direto)
  useEffect(() => {
    setOracleChannelId((prev) => (prev && prev !== currentChannel?.id ? null : prev));
    liveReResolvedRef.current = null;
  }, [currentChannel?.id]);
  const liveRouteRef = useRef<'direct' | 'oracle'>('oracle');
  liveRouteRef.current = liveRoute;
  const routeUrl = useCallback(
    (raw: string) => (liveRoute === 'direct' ? raw : getProxiedStreamUrl(raw, undefined, undefined, deviceId)),
    [liveRoute, deviceId]
  );

  // Clean stream URL resolution (single ultra-fast DNS)
  const baseStreamUrl = useMemo(() => {
    if (!currentChannel) return '';
    // V9.0.2: DNS da lista do canal/da conta, sempre 1 (sem lista no aparelho: mantém o do próprio canal)
    let raw = withListAccount(withServerHost(currentChannel.streamUrl, getStreamServer(currentChannel.playlistId)), getStreamAccount(currentChannel.playlistId));

    const channelProfile = currentChannel?.id ? getChannelStreamProfile(currentChannel.id) : undefined;
    const isAppleDevice =
      typeof navigator !== 'undefined' &&
      (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

    // Se o perfil do banco/cache define TS como prioritário, mantém o formato TS
    if (channelProfile?.preferred_format === 'ts' || channelProfile?.preferred_player === 'mpegts') {
      // Preserva formato bruto sem conversão forçada para .m3u8
    } else if (isAppleDevice) {
      raw = convertToHlsUrl(raw);
    } else if (raw.toLowerCase().endsWith('.ts')) {
      raw = raw.replace(/\.ts$/i, '.m3u8');
    }

    return routeUrl(raw);
  }, [currentChannel, getStreamServer, getStreamAccount, getChannelStreamProfile, routeUrl]);

  // V9.2.7: o provedor passou a mandar para o Google o pedido de NAVEGADOR também no ao vivo (teste 03/11 16:50:
  // entrada com User-Agent de Chrome = 302 google.com). O ENDEREÇO FINAL (para onde a entrada desvia um player) aceita
  // o Chrome: lista HLS, pedaços de vídeo e a mesma lista 60 s depois. Então, no direto, o servidor do site
  // (api/vod_link.php) pede o endereço como player e o navegador toca direto do servidor de vídeo — sem Oracle.
  const [liveFinal, setLiveFinal] = useState<Record<string, string>>({});
  const liveResolvingRef = useRef<string | null>(null);
  const liveReResolvedRef = useRef<string | null>(null);
  const precisaEnderecoFinal =
    liveRoute === 'direct' && /^https?:\/\/[^/]+\/(live\/)?[^/]+\/[^/]+\/[A-Za-z0-9_.-]+\.(m3u8|ts)$/i.test(baseStreamUrl);
  const streamUrl = precisaEnderecoFinal ? liveFinal[baseStreamUrl] || '' : baseStreamUrl;

  // Versão SEM a conversão forçada para HLS — usada como plano B em dispositivos Apple quando o
  // caminho nativo (AVPlayer) conecta mas nunca produz frame de vídeo (áudio toca, vídeo fica
  // 0x0). Alguns canais só têm o encode correto disponível no endpoint original (TS), e a
  // conversão para .m3u8 pode acabar batendo num endpoint que não foi de fato preparado pelo
  // provedor para esse formato.
  const rawFallbackStreamUrl = useMemo(() => {
    if (!currentChannel) return '';
    const raw = withListAccount(withServerHost(currentChannel.streamUrl, getStreamServer(currentChannel.playlistId)), getStreamAccount(currentChannel.playlistId));
    return routeUrl(raw);
  }, [currentChannel, getStreamServer, getStreamAccount, routeUrl]);

  const currentLoadedStreamUrlRef = useRef<string>('');
  const playCountRef = useRef<number>(0);
  const [showDiagnosticModal, setShowDiagnosticModal] = useState<boolean>(false);
  const [diagnosticLogs, setDiagnosticLogs] = useState<string[]>([]);
  const [isCopied, setIsCopied] = useState<boolean>(false);

  // Mantém a URL de fallback (sem conversão forçada para HLS) sempre disponível dentro do
  // watchdog, e reseta as travas de fallback a cada troca de canal.
  useEffect(() => {
    rawFallbackStreamUrlRef.current = rawFallbackStreamUrl;
    hasTriedNoVideoFallbackRef.current = false;
    noVideoWarningShownRef.current = false;
  }, [rawFallbackStreamUrl]);

  const isApple = useMemo(() => {
    return (
      typeof navigator !== 'undefined' &&
      (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
    );
  }, []);

  // Detecta se o app está rodando "instalado" (atalho na Tela de Início / modo standalone)
  // O WebKit do iOS desativa o Picture-in-Picture nesse modo, mesmo funcionando normalmente no Safari.
  // Referência: https://discussions.apple.com/thread/255709452
  const isStandaloneIOS = useMemo(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const isStandaloneDisplay =
      (window as any).navigator?.standalone === true ||
      window.matchMedia?.('(display-mode: standalone)')?.matches === true;
    return isStandaloneDisplay && /iPad|iPhone|iPod/.test(navigator.userAgent);
  }, []);

  const addDiagLog = useCallback((msg: string) => {
    const timestamp = new Date().toLocaleTimeString('pt-BR', { hour12: false });
    const line = `[${timestamp}] ${msg}`;
    console.log(line);
    setDiagnosticLogs((prev) => [...prev.slice(-49), line]);
  }, []);

  useEffect(() => {
    setStallInfo(null);
  }, [currentChannel?.id]);

  useEffect(() => {
    if (bufferingSeconds !== 16 || hasError || !streamUrl) return;
    let cancelled = false;
    const channelName = currentChannel?.name || '';
    setStallInfo({ title: 'VERIFICANDO O MOTIVO...', detail: 'Consultando o servidor do canal.' });
    (async () => {
      const list = await probeStreamUrl(streamUrl);
      let title = '';
      let detail = '';
      const listOk = list.kind === 'http' && (list.status || 0) >= 200 && (list.status || 0) < 300;
      if (!listOk) {
        const r = explainStreamFailure(list, 'carregando há 16 s');
        title = r.title.toUpperCase();
        detail = `${r.message} (${r.detail})`;
      } else if ((list.snippet || '').includes('#EXTM3U')) {
        const seg = await probeFirstSegment(streamUrl, list.snippet || '');
        if (!seg) {
          title = 'LISTA DO CANAL VAZIA';
          detail = 'O servidor respondeu, mas a lista do canal não tem nenhum pedaço de vídeo.';
        } else if (seg.probe.kind !== 'http') {
          title = 'O VÍDEO DO CANAL NÃO CHEGA';
          detail = `A lista chegou, mas os pedaços do vídeo não (${seg.probe.kind === 'timeout' ? 'tempo esgotado' : 'falha de conexão'}). Pode ser bloqueio do provedor para este canal.`;
        } else if ((seg.probe.status || 0) >= 400) {
          const r = explainStreamFailure(seg.probe, 'pedaço de vídeo');
          title = 'VÍDEO DO CANAL RECUSADO';
          detail = `A lista chegou, mas o provedor recusou os pedaços do vídeo — ${r.title} (HTTP ${seg.probe.status}).`;
        } else {
          title = 'CANAL LENTO NO SERVIDOR';
          detail = 'A lista e o vídeo respondem, mas o provedor está enviando devagar. Aguarde ou troque de canal.';
        }
      } else {
        title = 'CANAL LENTO NO SERVIDOR';
        detail = 'O servidor responde, mas o vídeo não começou a chegar. Aguarde ou troque de canal.';
      }
      if (cancelled) return;
      setStallInfo({ title, detail });
      addDiagLog(`[LIVE][ESPERA] ${title} | ${detail}`);
      remoteLog('web_live_stall', `${title} | ${detail}`, 'warning', { content_type: 'live', content_name: channelName });
    })().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [bufferingSeconds, hasError, streamUrl, currentChannel?.name, addDiagLog]);


  // V9.0.4: o direto não abriu → mesma tentativa pela Oracle (sem mostrar erro) e guarda a falha do servidor
  const fallToOracleRef = useRef<(reason: string) => void>(() => {});
  fallToOracleRef.current = (reason: string) => {
    if (!currentChannel || liveRouteRef.current !== 'direct') return;
    // V9.2.7: o endereço final já tinha tocado/aberto e falhou → pede um endereço NOVO uma vez antes da Oracle
    if (liveFinal[baseStreamUrl] && liveReResolvedRef.current !== String(currentChannel.id)) {
      liveReResolvedRef.current = String(currentChannel.id);
      addDiagLog(`[LIVE][ROTA] endereço final falhou (${reason}) — pedindo um endereço novo`);
      setLiveFinal((m) => {
        const n = { ...m };
        delete n[baseStreamUrl];
        return n;
      });
      return;
    }
    liveRouteRef.current = 'oracle';
    addDiagLog(`[LIVE][ROTA] direto não abriu (${reason}) — indo pela Oracle`);
    remoteLog('web_live_direct_fail', `${reason} | ${liveServerHost}`, 'info', { content_type: 'live', content_name: currentChannel.name || '' });
    setOracleChannelId(currentChannel.id);
  };

  // V9.2.7: busca o endereço final do canal (direto, navegador) no servidor do site
  useEffect(() => {
    if (!precisaEnderecoFinal || liveFinal[baseStreamUrl] || liveResolvingRef.current === baseStreamUrl) return;
    const alvo = baseStreamUrl;
    liveResolvingRef.current = alvo;
    const t0 = Date.now();
    apiClient.resolverVod(alvo).then((fim) => {
      if (liveResolvingRef.current === alvo) liveResolvingRef.current = null;
      if (fim) {
        addDiagLog(`[LIVE][ROTA] endereço final do canal em ${Date.now() - t0} ms (direto, sem Oracle)`);
        setLiveFinal((m) => ({ ...m, [alvo]: fim }));
      } else {
        fallToOracleRef.current('o servidor do site não trouxe o endereço final');
      }
    });
  }, [precisaEnderecoFinal, baseStreamUrl, liveFinal, addDiagLog]);

  // V8.2.6: mostra a falha e descobre o MOTIVO REAL consultando o endereço do vídeo (1 vez por tentativa)
  reportFailureRef.current = (engineDetail: string, probeUrl?: string) => {
    // V9.0.4: falhou no direto → tenta pela Oracle antes de mostrar erro
    if (liveRouteRef.current === 'direct') {
      fallToOracleRef.current(engineDetail);
      return;
    }
    setIsBuffering(false);
    setHasError(true);
    if (failureReportedRef.current) return; // já está verificando/mostrando o motivo desta tentativa
    failureReportedRef.current = true;
    const token = failureTokenRef.current;
    const url = probeUrl || streamUrl;
    setErrorTitle('Verificando o motivo...');
    setErrorMessage('O canal não abriu. Consultando o servidor para descobrir o motivo.');
    setErrorDetail(engineDetail);
    addDiagLog(`[LIVE][ERRO] falha: ${engineDetail} — consultando ${url ? url.split('?')[0] : '(sem url)'}`);
    const primaryUrl = streamUrl;
    const altUrl = rawFallbackStreamUrlRef.current || rawFallbackStreamUrl || '';
    const isOk = (p: StreamProbe) => p.kind === 'http' && (p.status || 0) >= 200 && (p.status || 0) < 300;
    (async () => {
      // 1º o endereço principal (o que o aparelho tentou tocar); se falhar, o endereço alternativo (formato original)
      let p = await probeStreamUrl(primaryUrl || url);
      if (!isOk(p) && altUrl && altUrl !== (primaryUrl || url)) {
        const alt = await probeStreamUrl(altUrl);
        if (isOk(alt)) {
          // O canal existe e responde no formato original, mas não no formato que este aparelho toca
          return {
            title: 'Vídeo incompatível com este aparelho',
            message:
              'O canal está no ar, mas só é entregue num formato (MPEG-TS) que este aparelho/navegador não consegue tocar. Tente em outro aparelho (TV Box, computador ou Android).',
            detail: `principal: ${p.kind === 'http' ? `HTTP ${p.status}` : p.kind} · alternativo: HTTP ${alt.status} ${alt.contentType || ''} · ${engineDetail}`,
          };
        }
        if (p.kind !== 'http' && alt.kind === 'http') p = alt;
      }
      return explainStreamFailure(p, engineDetail);
    })()
      .then((r) => {
        if (token !== failureTokenRef.current) return; // usuário trocou de canal/reconectou
        setErrorTitle(r.title);
        setErrorMessage(r.message);
        setErrorDetail(r.detail);
        addDiagLog(`[LIVE][ERRO] motivo: ${r.title} | ${r.detail}`);
        remoteLog('web_live_failed', `${r.title} | ${r.detail}`, 'error', { content_type: 'live', content_name: currentChannel?.name || '' });
      })
      .catch(() => {});
  };

  const isVideoActuallyRendering = useCallback((video: HTMLVideoElement): boolean => {
    if (hasRenderedVideoFrameRef.current) return true;
    const currentDecoded = (video as any)?.webkitDecodedFrameCount;
    if (typeof currentDecoded === 'number') {
      const diff = currentDecoded - (lastDecodedFrameBaseRef.current || 0);
      if (diff > 0) {
        hasRenderedVideoFrameRef.current = true;
        return true;
      }
      return false;
    }
    if (typeof (video as any).requestVideoFrameCallback === 'function') {
      return false;
    }
    return (video.videoWidth || 0) > 0 && (video.videoHeight || 0) > 0;
  }, []);

  const registerFrameCallback = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (typeof (video as any).requestVideoFrameCallback === 'function') {
      (video as any).requestVideoFrameCallback(() => {
        hasRenderedVideoFrameRef.current = true;
      });
    }
  }, []);

  const getFullDiagnosticReport = useCallback(() => {
    const video = videoRef.current;
    const bufEnd =
      video && video.buffered.length > 0
        ? video.buffered.end(video.buffered.length - 1)
        : 0;
    const wlDetails = getDetailedWakeLockStatus();
    const urlLower = streamUrl.toLowerCase();
    const detectedFormat = urlLower.includes('.m3u8')
      ? 'HLS (.m3u8)'
      : urlLower.includes('.ts') || urlLower.includes('output=ts')
      ? 'MPEG-TS'
      : 'Desconhecido/outro';
    const isRendering = video ? isVideoActuallyRendering(video) : false;
    const decodedFrames = (video as any)?.webkitDecodedFrameCount;
    const droppedFrames = (video as any)?.webkitDroppedFrameCount;
    const channelProfile = currentChannel?.id ? getChannelStreamProfile(currentChannel.id) : undefined;
    const isMseCached = currentChannel?.id ? channelNeedsMseFallback(currentChannel.id, channelProfile) : false;
    const activeEngine = hlsRef.current
      ? 'hls.js (MSE)'
      : mpegtsRef.current
      ? 'mpegts.js (MSE)'
      : isApple
      ? (detectedFormat === 'HLS (.m3u8)' ? 'Nativo <video src> (AVPlayer)' : 'mpegts.js / MSE')
      : 'hls.js / MSE';

    return [
      `=== RELATÓRIO DE DIAGNÓSTICO IPTV ===`,
      `Canal: ${currentChannel?.name} (ID: ${currentChannel?.id})`,
      `Dispositivo: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A'}`,
      `Modo de Exibição: ${isStandaloneIOS ? 'PWA Tela de Início (Standalone)' : 'Navegador Safari'}`,
      `Formato Detectado na URL: ${detectedFormat}`,
      `Caminho de Reprodução: ${activeEngine}`,
      `Conexão Prioritária no Banco: ${channelProfile ? `${channelProfile.preferred_player} (${channelProfile.preferred_format})` : 'Padrão / Auto'}`,
      `Tempo Vídeo: ${video ? video.currentTime.toFixed(2) : '0.00'}s`,
      `Dimensões Vídeo Reais (videoWidth x videoHeight): ${video ? `${video.videoWidth}x${video.videoHeight}` : 'N/A'}${decodedResolution ? ` [Ativo: ${decodedResolution}]` : ''}`,
      `Qualidade Solicitada: ${currentChannel?.currentQuality || currentChannel?.resolution || 'SD'}`,
      `Qualidades na Grade: ${availableQualities.map((q) => `${q.resolution} (${q.streamUrl})`).join(' | ')}`,
      `Frames Decodificados (WebKit): ${decodedFrames !== undefined ? decodedFrames : 'N/A'}`,
      `Frames Descartados (WebKit): ${droppedFrames !== undefined ? droppedFrames : 'N/A'}`,
      `Vídeo Renderizando Efetivamente: ${isRendering ? 'SIM' : 'NAO'}`,
      `Canal Memorizado no Cache MSE: ${isMseCached ? 'SIM' : 'NAO'}`,
      `ReadyState: ${video ? video.readyState : 'N/A'}`,
      `NetworkState: ${video ? video.networkState : 'N/A'}`,
      `Pausado: ${video?.paused ? 'SIM' : 'NAO'}`,
      `Buffer Final: ${bufEnd.toFixed(2)}s (diff: ${video ? (bufEnd - video.currentTime).toFixed(2) : '0'}s)`,
      `Chamadas play(): ${playCountRef.current}`,
      `WakeLock Tela: ${getScreenWakeLockStatus()}`,
      `WakeLock Suportado: ${wlDetails.wakeLockSupported ? 'SIM' : 'NAO'}`,
      `WakeLock Erro: ${wlDetails.lastWakeLockError}`,
      `Detalhes WakeLock: ${JSON.stringify(wlDetails)}`,
      ``,
      `--- LOGS DE EVENTOS ---`,
      ...diagnosticLogs,
    ].join('\n');
  }, [currentChannel, diagnosticLogs, isStandaloneIOS, streamUrl, isApple, isVideoActuallyRendering]);

  const copyDiagnosticReport = useCallback(() => {
    const reportText = getFullDiagnosticReport();
    let copied = false;

    // Método 1: Textarea escondida com seleção compatível com iOS Safari
    try {
      const textArea = document.createElement('textarea');
      textArea.value = reportText;
      textArea.style.position = 'fixed';
      textArea.style.left = '0';
      textArea.style.top = '0';
      textArea.style.opacity = '0.01';
      textArea.setAttribute('readonly', '');
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, 99999);
      copied = document.execCommand('copy');
      document.body.removeChild(textArea);
    } catch {
      copied = false;
    }

    // Método 2: navigator.clipboard se disponível
    if (!copied && navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(reportText).catch(() => {});
      copied = true;
    }

    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 3000);
    showToast('📋 Diagnóstico copiado para a área de transferência!', 'success');
  }, [getFullDiagnosticReport, showToast]);

  // Screen Wake Lock & iOS Keep-Awake Engine
  // Impede que a tela de qualquer modelo de iPhone / iPad / Android / TV / PC escureça ou bloqueie durante o vídeo
  const lastLoggedWakeLockStatusRef = useRef<string>('');
  const requestWakeLock = useCallback(() => {
    enableScreenWakeLock();
    // Só registra no diagnóstico quando o status realmente MUDA — esta função é chamada a cada
    // toque na tela, e logar toda vez inundava o buffer de 50 linhas do diagnóstico, empurrando
    // pra fora mensagens mais importantes (como o fallback de formato de vídeo).
    const status = getScreenWakeLockStatus();
    if (status !== lastLoggedWakeLockStatusRef.current) {
      lastLoggedWakeLockStatusRef.current = status;
      addDiagLog(`[WAKE_LOCK] ${status}`);
    }
  }, [addDiagLog]);

  const releaseWakeLock = useCallback(() => {
    disableScreenWakeLock();
  }, []);

  // Adiciona listeners para capturar o toque/clique do usuário e manter a tela permanentemente acesa
  // Padrão WHATWG: touchend, pointerup, click CONCEDEM ativação transiente; touchstart NÃO concede.
  useEffect(() => {
    const onUserGesture = () => {
      enableScreenWakeLock();
    };

    window.addEventListener('touchend', onUserGesture, { passive: true, capture: true });
    window.addEventListener('pointerup', onUserGesture, { passive: true, capture: true });
    window.addEventListener('click', onUserGesture, { passive: true, capture: true });

    enableScreenWakeLock();

    return () => {
      window.removeEventListener('touchend', onUserGesture, { capture: true });
      window.removeEventListener('pointerup', onUserGesture, { capture: true });
      window.removeEventListener('click', onUserGesture, { capture: true });
      disableScreenWakeLock();
    };
  }, []);

  // Safe play helper - ETAPA 2: Reduzir chamadas repetidas de play() no iOS
  const safePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    // Se já estiver tocando no iOS, NÃO chamar play() novamente para não quebrar buffer do AVPlayer
    if (isApple && !video.paused) {
      return;
    }

    playCountRef.current++;
    addDiagLog(`[LIVE][IOS] play() chamado (#${playCountRef.current})`);

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((err) => {
        if (err.name === 'NotAllowedError') {
          // Autoplay desmutado bloqueado pela política do navegador:
          // Inicia mutado temporariamente para começar a reprodução na hora sem ficar travado pausado
          video.muted = true;
          video.play().then(() => {
            setTimeout(() => {
              if (video && !isMutedRef.current) {
                video.muted = false;
              }
            }, 300);
          }).catch(() => {});
        } else if (err.name !== 'AbortError') {
          console.warn('[LivePlayer] Autoplay bloqueado pelo navegador:', err.message);
        }
      });
    }
  }, [isApple, addDiagLog]);

  // Cleanup active decoders com desconexão REAL no gerenciador de conexões IPTV
  const destroyPlayers = useCallback(() => {
    hasRenderedVideoFrameRef.current = false;
    hasTriedNoVideoFallbackRef.current = false;
    noVideoWarningShownRef.current = false;
    setDecodedResolution(null);
    if (videoRef.current) {
      lastDecodedFrameBaseRef.current = (videoRef.current as any).webkitDecodedFrameCount || 0;
    }
    if (hlsRef.current) {
      try {
        hlsRef.current.stopLoad();
        hlsRef.current.detachMedia();
        hlsRef.current.destroy();
      } catch {}
      hlsRef.current = null;
    }
    if (mpegtsRef.current) {
      try {
        mpegtsRef.current.unload();
        mpegtsRef.current.detachMediaElement();
        mpegtsRef.current.destroy();
      } catch {}
      mpegtsRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        // Essencial: remove o atributo src e chama load() para matar conexões pendentes no gerenciador IPTV
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
      } catch {}
    }
  }, []);

  const triggerAdaptiveFallback = useCallback((reason: string) => {
    const video = videoRef.current;
    if (!video || hasTriedNoVideoFallbackRef.current) return;
    hasTriedNoVideoFallbackRef.current = true;
    noVideoWarningShownRef.current = true;

    const fallbackUrl = rawFallbackStreamUrlRef.current || rawFallbackStreamUrl || streamUrl;
    const fallbackIsMpegTs = fallbackUrl.toLowerCase().includes('.ts') || fallbackUrl.toLowerCase().includes('output=ts');

    addDiagLog(`[LIVE][IOS] Fallback adaptativo acionado: ${reason}`);
    addDiagLog(`[LIVE][IOS] URL original: ${currentChannel?.streamUrl || 'N/A'}`);
    addDiagLog(`[LIVE][IOS] URL fallback: ${fallbackUrl}`);
    addDiagLog(`[LIVE][IOS] Engine fallback: ${fallbackIsMpegTs ? 'mpegts.js' : 'hls.js'}`);

    setIsBuffering(true);
    try {
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch {}
        hlsRef.current = null;
      }
      if (mpegtsRef.current) {
        try { mpegtsRef.current.destroy(); } catch {}
        mpegtsRef.current = null;
      }
      video.removeAttribute('src');
      video.load();

      const finalizeFailure = (detail: string) => {
        addDiagLog(`[LIVE][IOS] fallback também falhou: ${detail}`);
        reportFailureRef.current(detail, fallbackUrl);
      };

      if (fallbackIsMpegTs && mpegts.isSupported()) {
        const fallbackPlayer = mpegts.createPlayer(
          { type: 'mse', isLive: true, url: fallbackUrl },
          {
            enableWorker: true,
            lazyLoad: false,
            liveBufferLatencyChasing: false,
            liveBufferLatencyMaxLatency: 8.0,
            liveBufferLatencyMinRemain: 1.5,
          }
        );
        mpegtsRef.current = fallbackPlayer;
        fallbackPlayer.attachMediaElement(video);
        fallbackPlayer.load();
        const p = fallbackPlayer.play();
        if (p && typeof (p as any).catch === 'function') (p as any).catch(() => {});
        fallbackPlayer.on(mpegts.Events.ERROR, (errType: any, errDetail: any) => finalizeFailure(`mpegts.js type=${errType} detail=${errDetail}`));
      } else if (!fallbackIsMpegTs && Hls.isSupported()) {
        const fallbackHls = new Hls(FAST_LIVE_HLS_CONFIG);
        hlsRef.current = fallbackHls;
        fallbackHls.loadSource(fallbackUrl);
        fallbackHls.attachMedia(video);
        fallbackHls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsBuffering(false);
          safePlay();
        });
        fallbackHls.on(Hls.Events.ERROR, (_e, data) => {
          if (data.fatal) {
            finalizeFailure(`hls.js type=${data.type} details=${data.details}`);
          }
        });
      } else {
        finalizeFailure('nenhuma engine de fallback MSE disponível neste navegador');
      }

      // Verificação de sucesso do fallback após 4s
      setTimeout(() => {
        if (!videoRef.current) return;
        const v = videoRef.current;
        const rendering = isVideoActuallyRendering(v);
        if (!rendering && v.videoWidth === 0 && v.videoHeight === 0) {
          finalizeFailure('sem frames de vídeo após tentativa de fallback');
        } else if (currentChannel?.id) {
          addDiagLog(`[LIVE][IOS] Fallback validado com sucesso! Gravando conexão prioritária no Banco de Dados para "${currentChannel.name}"`);
          markChannelAsMseFallback(currentChannel.id);
          saveChannelStreamProfile({
            channelId: currentChannel.id,
            channelName: currentChannel.name,
            preferredPlayer: fallbackIsMpegTs ? 'mpegts' : 'hls_mse',
            preferredFormat: fallbackIsMpegTs ? 'ts' : 'm3u8',
            sourceUrl: fallbackUrl,
          }).catch(() => {});
        }
      }, 4000);
    } catch (err) {
      addDiagLog(`[LIVE][IOS] erro ao iniciar fallback: ${(err as any)?.message || err}`);
      reportFailureRef.current(`erro ao iniciar plano B: ${(err as any)?.message || err}`, fallbackUrl);
    }
  }, [currentChannel, rawFallbackStreamUrl, streamUrl, safePlay, addDiagLog, isVideoActuallyRendering, saveChannelStreamProfile]);

  const triggerAdaptiveFallbackRef = useRef(triggerAdaptiveFallback);
  useEffect(() => {
    triggerAdaptiveFallbackRef.current = triggerAdaptiveFallback;
  }, [triggerAdaptiveFallback]);

  // Load and play live channel stream
  useEffect(() => {
    if (!currentChannel || !streamUrl || !videoRef.current) return;
    // V8.8.6: transmitindo na TV (Cast) — o celular não abre o canal (só controla); abre ao parar o Cast
    if (isCasting) return;

    // Evita resetar o player e reabrir conexões no IPTV se a URL do canal for a mesma
    if (currentLoadedStreamUrlRef.current === streamUrl) {
      return;
    }
    destroyPlayers();
    currentLoadedStreamUrlRef.current = streamUrl;

    setHasError(false);
    setIsBuffering(true);
    setBufferingSeconds(0);
    setNetworkRetryCount(0);
    setLastNetworkError('');
    setErrorMessage('');
    setErrorTitle('');
    setErrorDetail('');
    failureTokenRef.current++;
    failureReportedRef.current = false;
    // V9.0.4: FUSION = direto no servidor da lista; ORACLE = pelo proxy (iPhone/iPad sempre)
    const routeAtLoad = liveRouteRef.current;
    setCurrentStreamRoute(routeAtLoad === 'direct' ? 'fusion' : 'oracle');
    addDiagLog(`[LIVE][ROTA] ${routeAtLoad === 'direct' ? 'DIRETO no servidor' : 'ORACLE'} (${liveServerHost || '-'})`);
    let directTimer: number | null = null;
    let onDirectPlaying: (() => void) | null = null;
    if (routeAtLoad === 'direct') {
      onDirectPlaying = () => {
        if (directTimer) window.clearTimeout(directTimer);
        directTimer = null;
      };
      videoRef.current.addEventListener('playing', onDirectPlaying, { once: true });
      directTimer = window.setTimeout(() => {
        directTimer = null;
        const v = videoRef.current;
        if (v && v.readyState < 3 && liveRouteRef.current === 'direct') {
          fallToOracleRef.current(`sem imagem em ${LIVE_DIRECT_TIMEOUT_MS / 1000} s`);
        }
      }, LIVE_DIRECT_TIMEOUT_MS);
    }

    const video = videoRef.current;
    const isMpegTs = streamUrl.toLowerCase().includes('.ts') || streamUrl.toLowerCase().includes('output=ts');

    hasRenderedVideoFrameRef.current = false;
    lastDecodedFrameBaseRef.current = (video as any).webkitDecodedFrameCount || 0;

    addDiagLog(`[LIVE][IOS] load channelId=${currentChannel.id} name="${currentChannel.name}" isApple=${isApple}`);
    addDiagLog(`[LIVE][IOS] channel.streamUrl (bruto, do canal selecionado): ${currentChannel.streamUrl ? `"${currentChannel.streamUrl.substring(0, 80)}..." (${currentChannel.streamUrl.length} chars)` : 'VAZIO/UNDEFINED — canal sem URL de stream!'}`);
    addDiagLog(`[LIVE][IOS] streamUrl final (após proxy/DNS): ${streamUrl ? `"${streamUrl.substring(0, 80)}..." (${streamUrl.length} chars)` : 'VAZIO — nada será carregado!'}`);

    // No iOS (Safari / PWA), usa sempre HLS nativo via AVPlayer, EXCETO se o canal já foi memorizado como MSE no banco ou cache
    const channelProfile = currentChannel?.id ? getChannelStreamProfile(currentChannel.id) : undefined;
    const knownMseChannel = isApple && channelNeedsMseFallback(currentChannel.id, channelProfile);

    if (isApple && !knownMseChannel && video.canPlayType('application/vnd.apple.mpegurl')) {
      video.preload = 'auto';
      video.src = streamUrl;
      video.load();
      safePlay();
    } else if (knownMseChannel) {
      const fallbackUrl = rawFallbackStreamUrlRef.current || rawFallbackStreamUrl;
      const fallbackIsMpegTs = fallbackUrl.toLowerCase().includes('.ts') || fallbackUrl.toLowerCase().includes('output=ts');
      const originSource = channelProfile?.preferred_player ? 'no BANCO DE DADOS central' : 'no cache local';
      addDiagLog(`[LIVE][IOS] Conexão prioritária MSE identificada ${originSource}: abrindo direto via ${fallbackIsMpegTs ? 'mpegts.js' : 'hls.js'} sem passar pelo AVPlayer`);
      hasTriedNoVideoFallbackRef.current = true;
      if (fallbackIsMpegTs && mpegts.isSupported()) {
        try {
          const fallbackPlayer = mpegts.createPlayer(
            { type: 'mse', isLive: true, url: fallbackUrl },
            {
              enableWorker: true,
              lazyLoad: false,
              liveBufferLatencyChasing: false,
              liveBufferLatencyMaxLatency: 8.0,
              liveBufferLatencyMinRemain: 1.5,
            }
          );
          mpegtsRef.current = fallbackPlayer;
          fallbackPlayer.attachMediaElement(video);
          fallbackPlayer.load();
          const p = fallbackPlayer.play();
          if (p && typeof (p as any).catch === 'function') (p as any).catch(() => {});
          fallbackPlayer.on(mpegts.Events.ERROR, (errType: any, errDetail: any) => {
            reportFailureRef.current(`mpegts.js type=${errType} detail=${errDetail}`, fallbackUrl);
          });
        } catch {
          video.src = fallbackUrl;
          video.load();
          safePlay();
        }
      } else if (!fallbackIsMpegTs && Hls.isSupported()) {
        const fallbackHls = new Hls(FAST_LIVE_HLS_CONFIG);
        hlsRef.current = fallbackHls;
        fallbackHls.loadSource(fallbackUrl);
        fallbackHls.attachMedia(video);
        fallbackHls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsBuffering(false);
          safePlay();
        });
        fallbackHls.on(Hls.Events.ERROR, (_e, data) => {
          if (data.fatal) {
            reportFailureRef.current(
              `hls.js type=${data.type} details=${data.details}${(data as any).response?.code ? ` http=${(data as any).response.code}` : ''}`,
              fallbackUrl
            );
          }
        });
      } else {
        video.src = fallbackUrl;
        video.load();
        safePlay();
      }
    } else if (!isMpegTs && Hls.isSupported()) {
      const hls = new Hls(FAST_LIVE_HLS_CONFIG);

      hlsRef.current = hls;
      hls.loadSource(streamUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        safePlay();
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsBuffering(false);
        safePlay();

        if (hls.audioTracks && hls.audioTracks.length > 0) {
          setAudioTracks(
            hls.audioTracks.map((t, idx) => ({
              id: idx,
              name: t.name || `Áudio ${idx + 1}`,
              lang: t.lang || '',
            }))
          );
          setCurrentAudioTrack(hls.audioTrack);
        }
      });

      // FRAG_PARSED: apenas atualiza estado de buffering.
      // NÃO chama safePlay() a cada fragmento (a cada 2-4s) para não interromper o pipeline de decodificação.
      hls.on(Hls.Events.FRAG_PARSED, () => {
        setIsBuffering(false);
      });

      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        setIsBuffering(false);
      });

      hls.on(Hls.Events.LEVEL_SWITCHED, (_e, data) => {
        const level = hls.levels[data.level];
        if (level) {
          const h = level.height || 0;
          const label = h >= 2160 ? '4K' : h >= 1080 ? '1080p' : h >= 720 ? '720p' : h >= 480 ? '480p' : 'HD';
          setActiveResolution(label);
        }
      });

      let fatalNetCount = 0; // V8.3.3
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
          const v = videoRef.current;
          if (v && !v.paused) {
            try {
              v.currentTime += 0.15;
              safePlay();
            } catch {}
          }
          return;
        }

        if (data.fatal) {
          // V9.0.4: no direto, erro fatal (servidor não autoriza o navegador, recusa, rede) → Oracle na hora
          if (liveRouteRef.current === 'direct') {
            fallToOracleRef.current(`hls.js ${data.type}/${data.details}${(data as any).response?.code ? ` http=${(data as any).response.code}` : ''}`);
            return;
          }
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              setNetworkRetryCount((prev) => prev + 1);
              setLastNetworkError(data.details || 'Falha de rede');
              fatalNetCount++;
              // V8.3.3: antes tentava para sempre e nunca mostrava o motivo. Depois de 4 tentativas, para e explica.
              if (fatalNetCount > 4) {
                addDiagLog(`[LIVE][HLS] NETWORK_ERROR ${fatalNetCount}x details=${data.details}. Desistindo e mostrando o motivo.`);
                reportFailureRef.current(
                  `hls.js rede ${data.details}${(data as any).response?.code ? ` http=${(data as any).response.code}` : ''}`,
                  streamUrl
                );
                break;
              }
              addDiagLog(`[LIVE][HLS] NETWORK_ERROR details=${data.details}. Tentando startLoad()...`);
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              try {
                hls.recoverMediaError();
              } catch {
                try {
                  hls.swapAudioCodec();
                  hls.recoverMediaError();
                } catch {}
              }
              break;
            default:
              reportFailureRef.current(`hls.js type=${data.type} details=${data.details}`, streamUrl);
              break;
          }
        }
      });
    } else if (isMpegTs && mpegts.isSupported()) {
      try {
        const player = mpegts.createPlayer(
          {
            type: 'mse',
            isLive: true,
            url: streamUrl,
          },
          {
            enableWorker: true,
            lazyLoad: false,
            liveBufferLatencyChasing: false,
            liveBufferLatencyMaxLatency: 8.0,
            liveBufferLatencyMinRemain: 1.5,
          }
        );

        mpegtsRef.current = player;
        player.attachMediaElement(video);
        player.load();
        const playRes = player.play();
        if (playRes && typeof (playRes as any).catch === 'function') {
          (playRes as any).catch(() => {});
        }

        player.on(mpegts.Events.ERROR, (errType: any, errDetail: any) => {
          reportFailureRef.current(`mpegts.js type=${errType} detail=${errDetail}`, streamUrl);
        });
      } catch {
        video.src = streamUrl;
        video.load();
        safePlay();
      }
    } else {
      video.src = streamUrl;
      video.load();
      safePlay();
    }

    return () => {
      if (directTimer) window.clearTimeout(directTimer);
      if (onDirectPlaying) videoRef.current?.removeEventListener('playing', onDirectPlaying);
      currentLoadedStreamUrlRef.current = '';
      destroyPlayers();
      hasTriedNoVideoFallbackRef.current = false;
      noVideoWarningShownRef.current = false;
    };
  }, [currentChannel?.id, streamUrl, rawFallbackStreamUrl, safePlay, destroyPlayers, isApple, addDiagLog, isCasting]);

  // Video event handlers e Instrumentação de Diagnóstico (ETAPA 3 DO PLANO)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let lastCurrentTime = video.currentTime;
    let stallCount = 0;
    let noVideoFrameCount = 0;
    let persistentStallRetries = 0;
    let persistentStallWarningShown = false;
    hasTriedNoVideoFallbackRef.current = false;
    noVideoWarningShownRef.current = false;

    const onPlaying = () => {
      setIsPlaying(true);
      setIsBuffering(false);
      setHasError(false);
      stallCount = 0;
      requestWakeLock();
      registerFrameCallback();
      addDiagLog(`[LIVE][IOS] event=playing currentTime=${video.currentTime.toFixed(2)}`);
    };

    const onTimeUpdate = () => {
      if (video.currentTime > 0) {
        setIsBuffering(false);
        setHasError(false);
        stallCount = 0;
        registerFrameCallback();
        const currentDecoded = (video as any)?.webkitDecodedFrameCount;
        if (typeof currentDecoded === 'number' && currentDecoded > (lastDecodedFrameBaseRef.current || 0)) {
          hasRenderedVideoFrameRef.current = true;
        }
      }
    };

    const onLoadedData = () => {
      setIsBuffering(false);
      setHasError(false);
      registerFrameCallback();
      addDiagLog(`[LIVE][IOS] event=loadeddata readyState=${video.readyState}`);
    };

    const onLoadedMetadata = () => {
      addDiagLog(`[LIVE][IOS] event=loadedmetadata duration=${video.duration ? video.duration.toFixed(2) : 'live'}`);
    };

    const onWaiting = () => {
      if (video.currentTime === 0 || video.paused) {
        setIsBuffering(true);
      }
      const bufEnd = video.buffered.length > 0 ? video.buffered.end(video.buffered.length - 1) : 0;
      addDiagLog(`[LIVE][IOS] event=waiting currentTime=${video.currentTime.toFixed(2)} readyState=${video.readyState} networkState=${video.networkState} bufferedEnd=${bufEnd.toFixed(2)}`);
    };

    const onStalled = () => {
      const bufEnd = video.buffered.length > 0 ? video.buffered.end(video.buffered.length - 1) : 0;
      addDiagLog(`[LIVE][IOS] event=stalled currentTime=${video.currentTime.toFixed(2)} readyState=${video.readyState} networkState=${video.networkState} bufferedEnd=${bufEnd.toFixed(2)}`);
      // Se estagnou no AVPlayer nativo do iOS e ainda não está renderizando vídeo
      if (isApple && !hasTriedNoVideoFallbackRef.current && !hlsRef.current && !mpegtsRef.current) {
        if (!isVideoActuallyRendering(video)) {
          addDiagLog(`[LIVE][IOS] event=stalled sem renderização de vídeo no AVPlayer. Disparando fallback MSE.`);
          triggerAdaptiveFallbackRef.current('event=stalled sem renderização de vídeo no AVPlayer nativo');
        }
      }
    };

    const onCanPlay = () => {
      setIsBuffering(false);
      setHasError(false);
      registerFrameCallback();
      addDiagLog(`[LIVE][IOS] event=canplay currentTime=${video.currentTime.toFixed(2)} readyState=${video.readyState}`);
      if (!isApple && video.paused) {
        safePlay();
      }
    };

    const onPause = () => {
      setIsPlaying(false);
      const isHidden = typeof document !== 'undefined' ? document.hidden : false;
      addDiagLog(`[LIVE][IOS] event=pause currentTime=${video.currentTime.toFixed(2)} documentHidden=${isHidden}`);
    };

    const onError = () => {
      const errCode = video.error?.code;
      const errMsg = video.error?.message || '';
      addDiagLog(`[LIVE][IOS] event=error code=${errCode} message="${errMsg}"`);
      if (!hasTriedNoVideoFallbackRef.current && (isApple || !hlsRef.current)) {
        addDiagLog(`[LIVE][IOS] Erro no player primário. Tentando fallback adaptativo MSE.`);
        triggerAdaptiveFallbackRef.current(`erro primário code=${errCode}`);
        return;
      }
      reportFailureRef.current(`erro do vídeo code=${errCode}${errMsg ? ` "${errMsg}"` : ''}`, streamUrl);
    };

    const onSuspend = () => {
      addDiagLog(`[LIVE][IOS] event=suspend networkState=${video.networkState}`);
    };

    // Watchdog Passivo no iOS com Logs Recomendados
    const watchdogInterval = setInterval(() => {
      if (!video || video.paused || video.ended || hasError) {
        stallCount = 0;
        return;
      }

      const cur = video.currentTime;
      const isRendering = isVideoActuallyRendering(video);

      if (Math.abs(cur - lastCurrentTime) < 0.1) {
        stallCount++;
        // Se ficou estagnado por mais de 3 checagens consecutivas (3 segundos)
        if (stallCount >= 3) {
          if (isApple && !hasTriedNoVideoFallbackRef.current && !hlsRef.current && !mpegtsRef.current) {
            addDiagLog(`[LIVE][IOS] AVPlayer estagnado por ${stallCount}s (cur=${cur.toFixed(2)}). Disparando fallback adaptativo MSE.`);
            triggerAdaptiveFallbackRef.current(`AVPlayer estagnado por ${stallCount}s (cur=${cur.toFixed(2)})`);
          } else if (hlsRef.current) {
            hlsRef.current.recoverMediaError();
          } else if (!isApple) {
            persistentStallRetries++;
            safePlay();
            // Diferente do caso "sem vídeo" (que já tem aviso claro), o travamento genérico
            // tentava para sempre em silêncio. Depois de ~10 tentativas seguidas sem sucesso
            // (mais de 30s de sinal instável), avisa o usuário — sem parar de tentar sozinho,
            // já que pode ser uma instabilidade passageira de rede/CDN que se resolve sozinha.
            if (persistentStallRetries >= 10 && !persistentStallWarningShown) {
              persistentStallWarningShown = true;
              addDiagLog(`[LIVE][IOS] sinal instável: ${persistentStallRetries} tentativas de reconexão sem sucesso.`);
              showToast('Sinal instável neste canal — tentando reconectar automaticamente. Se persistir, tente novamente mais tarde.', 'warning');
            }
          }
        }
      } else {
        stallCount = 0;
        lastCurrentTime = cur;
        setIsBuffering(false);
        persistentStallRetries = 0;
        persistentStallWarningShown = false;
      }

      // Detecção ultrarrápida de "áudio tocando sem frames de vídeo (tela preta)":
      // Se áudio está avançando (cur >= 0.5s) ou readyState >= 3, mas os frames NÃO estão sendo renderizados:
      if (video.readyState >= 3 && !isRendering && cur >= 0.5) {
        noVideoFrameCount++;
        if (noVideoFrameCount >= 2 && !hasTriedNoVideoFallbackRef.current) {
          addDiagLog(`[LIVE][IOS] sem-frames-video detectado: áudio tocando (cur=${cur.toFixed(2)}) mas frames não são renderizados. Disparando fallback MSE.`);
          triggerAdaptiveFallbackRef.current('áudio tocando sem frames de vídeo (tela preta)');
        }
      } else if (isRendering) {
        noVideoFrameCount = 0;
        if (hasTriedNoVideoFallbackRef.current && currentChannel?.id) {
          markChannelAsMseFallback(currentChannel.id);
          saveChannelStreamProfile({
            channelId: currentChannel.id,
            channelName: currentChannel.name,
            preferredPlayer: hlsRef.current ? 'hls_mse' : mpegtsRef.current ? 'mpegts' : 'auto',
            preferredFormat: 'm3u8',
          }).catch(() => {});
        }
      } else {
        noVideoFrameCount = 0;
      }
    }, 1000);

    const onDimensionsChange = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        const dimStr = `${video.videoWidth}x${video.videoHeight}`;
        setDecodedResolution(dimStr);
        addDiagLog(`[LIVE] Resolução real decodificada em tela: ${dimStr}`);
      }
    };

    video.addEventListener('playing', onPlaying);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadeddata', onLoadedData);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('loadedmetadata', onDimensionsChange);
    video.addEventListener('resize', onDimensionsChange);
    video.addEventListener('playing', onDimensionsChange);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('stalled', onStalled);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('error', onError);
    video.addEventListener('suspend', onSuspend);

    return () => {
      clearInterval(watchdogInterval);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('loadedmetadata', onDimensionsChange);
      video.removeEventListener('resize', onDimensionsChange);
      video.removeEventListener('playing', onDimensionsChange);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('stalled', onStalled);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('error', onError);
      video.removeEventListener('suspend', onSuspend);
    };
  }, [currentChannel?.id, hasError, safePlay, isApple, addDiagLog, requestWakeLock, showToast]);

  // Monitora visibilidade da tela: re-adquire WakeLock e retoma reprodução se a tela foi reacesa
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isHidden = typeof document !== 'undefined' ? document.hidden : false;
      addDiagLog(`[LIVE][IOS] visibilitychange: isHidden=${isHidden} state=${document.visibilityState}`);
      if (!isHidden) {
        requestWakeLock();
        const v = videoRef.current;
        // Se a tela foi reacesa e o vídeo estava pausado pelo bloqueio do iOS, retoma automaticamente!
        if (v && v.paused && currentChannel) {
          addDiagLog('[LIVE][IOS] Tela reativada. Retomando reprodução...');
          safePlay();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [requestWakeLock, currentChannel, safePlay, addDiagLog]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showEpgDrawer || isTouchLocked) return;

      switch (e.key) {
        case 'ArrowUp':
        case 'PageUp':
        case '[':
          e.preventDefault();
          zapChannel('prev');
          pokePlayerControls();
          break;
        case 'ArrowDown':
        case 'PageDown':
        case ']':
          e.preventDefault();
          zapChannel('next');
          pokePlayerControls();
          break;
        case ' ':
        case 'Enter':
          e.preventDefault();
          togglePlay();
          pokePlayerControls();
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          toggleMute();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'g':
        case 'G':
          e.preventDefault();
          setShowEpgDrawer((prev) => !prev);
          break;
        case 'Escape':
        case 'Back':
        case 'BrowserBack':
          e.preventDefault();
          stopPlayback();
          break;
        default:
          pokePlayerControls();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showEpgDrawer, isTouchLocked, zapChannel, stopPlayback, pokePlayerControls]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(isVideoInFullscreen(containerRef.current));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Controls auto-hide timer — delega para a fonte única (IPTVContext) que também atende
  // controle remoto/teclado, evitando dois cronômetros concorrentes.
  const lastTouchTimeRef = useRef<number>(0);
  const touchStartPosRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const lastToggleTimeRef = useRef<number>(0);

  const handleUserActivity = useCallback(() => {
    requestWakeLock();
    if (isTouchLocked) return;
    // Ignora eventos mousemove sintéticos gerados pelo navegador logo após toques na tela (Android/iOS)
    if (Date.now() - lastTouchTimeRef.current < 1000) return;
    pokePlayerControls();
  }, [isTouchLocked, pokePlayerControls, requestWakeLock]);

  // Impede o auto-hide de disparar enquanto há buffering (ex: canais 4K pesados) — reaplica
  // sempre que isBuffering mudar.
  useEffect(() => {
    setControlsAutoHideSuppressed(isBuffering);
  }, [isBuffering, setControlsAutoHideSuppressed]);

  // Fecha o menu de áudio junto com os controles, seja por toque manual ou auto-hide do timer.
  useEffect(() => {
    if (!isPlayerControlsVisible) setShowAudioMenu(false);
  }, [isPlayerControlsVisible]);

  // Padlock: Banner & Temporarily show discrete unlock button
  const showLockedBanner = useCallback((text: string) => {
    setLockedBannerText(text);
    if (bannerTimeoutRef.current) clearTimeout(bannerTimeoutRef.current);
    bannerTimeoutRef.current = setTimeout(() => setLockedBannerText(null), 2200);
  }, []);

  const showUnlockButtonTemporarily = useCallback(() => {
    setIsUnlockUIVisible(true);
    if (unlockUIRef.current) clearTimeout(unlockUIRef.current);
    unlockUIRef.current = setTimeout(() => {
      setIsUnlockUIVisible(false);
      setUnlockTapCount(0);
    }, 3500);
  }, []);

  // Alternar visibilidade dos controles ao tocar na tela / vídeo
  const handleToggleControls = useCallback(
    (e?: React.SyntheticEvent | React.TouchEvent | React.MouseEvent) => {
      requestWakeLock();
      if (e && 'stopPropagation' in e) e.stopPropagation();

      // Trava anti-duplo clique: ignora eventos repetidos em menos de 400ms (ex: touchend seguido de click)
      if (Date.now() - lastToggleTimeRef.current < 400) return;
      lastToggleTimeRef.current = Date.now();

      if (isTouchLocked) {
        showUnlockButtonTemporarily();
        return;
      }
      // Se estiver visível e não estiver travado em buffering, oculta imediatamente
      if (isPlayerControlsVisible && !isBuffering) {
        hidePlayerControlsNow();
      } else {
        pokePlayerControls(5000);
      }
    },
    [isTouchLocked, isPlayerControlsVisible, isBuffering, showUnlockButtonTemporarily, pokePlayerControls, hidePlayerControlsNow, requestWakeLock]
  );

  // 1 Clique Simples para Travar
  const handleLockScreen = useCallback(
    (e?: React.SyntheticEvent) => {
      if (e) e.stopPropagation();
      setIsTouchLocked(true);
      setIsPlayerControlsVisible(false);
      setIsUnlockUIVisible(false);
      setUnlockTapCount(0);
      showLockedBanner('TELA BLOQUEADA 🔒');
      showToast('Tela Bloqueada 🔒', 'info');

      // Tenta travar orientação em landscape se suportado
      if (typeof window !== 'undefined' && window.screen && (window.screen.orientation as any)?.lock) {
        (window.screen.orientation as any).lock('landscape').catch(() => {});
      }
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        try {
          navigator.vibrate(50);
        } catch {}
      }
    },
    [setIsPlayerControlsVisible, showLockedBanner, showToast]
  );

  // 2 Cliques Rápidos (Duplo Toque) para Destravar
  const handleUnlockTap = useCallback(
    (e?: React.SyntheticEvent) => {
      if (e) e.stopPropagation();
      const now = Date.now();
      const diff = now - lastUnlockClickRef.current;

      if (diff > 50 && diff < 800) {
        // 2º toque efetuado -> Destrava!
        lastUnlockClickRef.current = 0;
        setUnlockTapCount(0);
        setIsTouchLocked(false);
        setIsUnlockUIVisible(false);
        showLockedBanner('TELA DESBLOQUEADA 🔓');
        showToast('Tela Desbloqueada 🔓', 'info');
        pokePlayerControls();

        if (typeof window !== 'undefined' && window.screen && window.screen.orientation?.unlock) {
          window.screen.orientation.unlock();
        }
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
          try {
            navigator.vibrate([40, 40, 70]);
          } catch {}
        }
      } else {
        // 1º toque -> Registra e aguarda 2º toque (fica verde!)
        lastUnlockClickRef.current = now;
        setUnlockTapCount(1);
        showUnlockButtonTemporarily();

        if (tapResetTimerRef.current) clearTimeout(tapResetTimerRef.current);
        tapResetTimerRef.current = setTimeout(() => {
          setUnlockTapCount(0);
          lastUnlockClickRef.current = 0;
        }, 800);
      }
    },
    [pokePlayerControls, showLockedBanner, showToast, showUnlockButtonTemporarily]
  );

  // Toggle play/pause
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      safePlay();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (!videoRef.current) return;
    const nextMuted = !isMuted;
    videoRef.current.muted = nextMuted;
    setIsMuted(nextMuted);
    showToast(nextMuted ? 'Áudio silenciado' : 'Áudio ativado', 'info');
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    requestWakeLock();
    toggleVideoFullscreen(containerRef.current, videoRef.current);
  };

  // Reconnect / Hard Reset completo da conexão
  const handleReconnect = useCallback(() => {
    showToast('⚡ Forçando reconexão do canal...', 'info');
    setIsBuffering(true);
    setHasError(false);
    setErrorTitle('');
    setErrorDetail('');
    failureTokenRef.current++;
    failureReportedRef.current = false;
    setIsManualReconnecting(true);
    setTimeout(() => setIsManualReconnecting(false), 1200);

    destroyPlayers();

    setTimeout(() => {
      if (!currentChannel || !videoRef.current || !streamUrl) return;
      const video = videoRef.current;
      const isMpegTs = streamUrl.toLowerCase().includes('.ts') || streamUrl.toLowerCase().includes('output=ts');
      const isApple =
        typeof navigator !== 'undefined' &&
        (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

      if (!isMpegTs && isApple && video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = streamUrl;
        video.load();
        safePlay();
      } else if (!isMpegTs && Hls.isSupported()) {
        const hls = new Hls(FAST_LIVE_HLS_CONFIG);
        hlsRef.current = hls;
        hls.loadSource(streamUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsBuffering(false);
          safePlay();
        });
        hls.on(Hls.Events.FRAG_PARSED, () => {
          setIsBuffering(false);
        });
        hls.on(Hls.Events.FRAG_BUFFERED, () => {
          setIsBuffering(false);
        });
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
            const v = videoRef.current;
            if (v && !v.paused) {
              try {
                v.currentTime += 0.15;
                safePlay();
              } catch {}
            }
            return;
          }
          if (data.fatal) {
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
              hls.startLoad();
            } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
              try {
                hls.recoverMediaError();
              } catch {}
            }
          }
        });
        safePlay();
      } else {
        video.src = streamUrl;
        video.load();
        safePlay();
      }
    }, 150);
  }, [currentChannel, streamUrl, safePlay, destroyPlayers, showToast]);

  // Aspect ratio switch
  const cycleAspectRatio = () => {
    const next = aspectRatio === 'contain' ? 'cover' : aspectRatio === 'cover' ? 'fill' : 'contain';
    setAspectRatio(next);
    const label = next === 'contain' ? 'Padrão (16:9)' : next === 'cover' ? 'Zoom (Preencher Tela)' : 'Esticado (100%)';
    showToast(`Proporção: ${label}`, 'info');
  };

  // Listeners de evento para Picture-in-Picture
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onEnterPip = () => setIsPipActive(true);
    const onLeavePip = () => setIsPipActive(false);
    const onPresentationChange = () => {
      const mode = (video as any).webkitPresentationMode;
      if (mode) setIsPipActive(mode === 'picture-in-picture');
    };

    video.addEventListener('enterpictureinpicture', onEnterPip);
    video.addEventListener('leavepictureinpicture', onLeavePip);
    video.addEventListener('webkitpresentationmodechanged', onPresentationChange);

    return () => {
      video.removeEventListener('enterpictureinpicture', onEnterPip);
      video.removeEventListener('leavepictureinpicture', onLeavePip);
      video.removeEventListener('webkitpresentationmodechanged', onPresentationChange);
    };
  }, []);

  // Picture in Picture (com suporte universal W3C e Safari WebKit iOS)
  const togglePiP = async () => {
    try {
      const video = videoRef.current;
      if (!video) return;

      const v = video as any;

      // Limitação conhecida da Apple: PiP é desativado pelo WebKit no modo instalado (tela inicial) no iOS.
      // Não há workaround via JS — orienta o usuário a usar o Safari normal.
      if (isStandaloneIOS) {
        showToast('Janela Flutuante indisponível no app instalado. Abra pelo Safari para usar esse recurso.', 'info');
        return;
      }

      // 0. App Android nativo (ponte pronta em MainActivity.java)
      if (typeof window !== 'undefined' && (window as any).AndroidBridge?.enterPictureInPicture) {
        try {
          (window as any).AndroidBridge.enterPictureInPicture();
          setIsPipActive(true);
          return;
        } catch (err) {
          console.warn('[PiP] AndroidBridge.enterPictureInPicture falhou:', err);
        }
      }

      // 1. W3C Standard (Chrome, Edge, Firefox, Android, Safari desktop moderno)
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPipActive(false);
        return;
      }

      // 2. Safari iOS (iPad / Mac / iPhone) via webkitSetPresentationMode
      if (typeof v.webkitSetPresentationMode === 'function') {
        try {
          const currentMode = v.webkitPresentationMode;
          const targetMode = currentMode === 'picture-in-picture' ? 'inline' : 'picture-in-picture';
          v.webkitSetPresentationMode(targetMode);
          setIsPipActive(targetMode === 'picture-in-picture');
          return;
        } catch {}
      }

      if (video.requestPictureInPicture) {
        await video.requestPictureInPicture();
        setIsPipActive(true);
        return;
      }

      showToast('Janela Flutuante (PiP) não suportada neste navegador', 'warning');
    } catch (err: any) {
      console.warn('PiP error', err);
      if (err?.name !== 'NotAllowedError') {
        showToast('Não foi possível ativar Janela Flutuante', 'warning');
      }
    }
  };

  // Compartilhar Tela / Transmitir na TV
  const handleCast = () => {
    setIsCastModalOpen(true);
  };

  if (!currentChannel) return null;

  return (
    <div
      ref={containerRef}
      onMouseMove={isTouchLocked ? undefined : handleUserActivity}
      onClick={(e) => {
        if (Date.now() - lastTouchTimeRef.current < 700) return;
        if (isTouchLocked) {
          showUnlockButtonTemporarily();
        } else {
          handleToggleControls(e);
        }
      }}
      className="fixed inset-0 z-50 bg-black flex items-center justify-center select-none overflow-hidden"
      style={{ touchAction: 'manipulation' }}
    >
      {/* HTML5 Video Element */}
      <video
        ref={videoRef}
        poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
        playsInline
        autoPlay
        muted={isMuted}
        {...{
          'webkit-playsinline': 'true',
          'x-webkit-airplay': 'allow',
          airplay: 'allow',
          disablePictureInPicture: false,
        }}
        style={{
          objectFit: aspectRatio === 'fill' ? 'fill' : aspectRatio === 'cover' ? 'cover' : 'contain',
        }}
        className="w-full h-full pointer-events-none"
      />

      {/* Camada Transparente Capturadora de Toques (Garante resposta instantânea ao toque sem interferência do vídeo) */}
      {!isTouchLocked && (
        <div
          className="absolute inset-0 z-10 cursor-pointer"
          style={{ touchAction: 'manipulation' }}
          onTouchStart={(e) => {
            lastTouchTimeRef.current = Date.now();
            if (e.touches.length === 1) {
              touchStartPosRef.current = {
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
                time: Date.now(),
              };
            }
          }}
          onTouchEnd={(e) => {
            lastTouchTimeRef.current = Date.now();
            enableScreenWakeLock();
            if (touchStartPosRef.current && e.changedTouches.length === 1) {
              const dx = Math.abs(e.changedTouches[0].clientX - touchStartPosRef.current.x);
              const dy = Math.abs(e.changedTouches[0].clientY - touchStartPosRef.current.y);
              const dt = Date.now() - touchStartPosRef.current.time;
              touchStartPosRef.current = null;
              if (dt < 450 && dx < 25 && dy < 25) {
                e.preventDefault();
                e.stopPropagation();
                handleToggleControls(e);
              }
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            enableScreenWakeLock();
            if (Date.now() - lastTouchTimeRef.current < 700) return;
            handleToggleControls(e);
          }}
        />
      )}

      {/* Camada Invisível de Bloqueio Anti-Toque */}
      {isTouchLocked && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            showUnlockButtonTemporarily();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            showUnlockButtonTemporarily();
          }}
          className="absolute inset-0 z-40 bg-transparent cursor-default pointer-events-auto select-none"
        >
          {/* Banner rápido TELA BLOQUEADA / DESBLOQUEADA */}
          {lockedBannerText && (
            <div
              className="absolute left-1/2 -translate-x-1/2 z-50 pointer-events-none animate-in fade-in zoom-in-95 duration-200"
              style={{ top: 'max(1.5rem, env(safe-area-inset-top, 1.5rem))' }}
            >
              <div className="px-4 py-2 rounded-xl bg-zinc-950/90 text-amber-300 border border-amber-500/40 shadow-2xl backdrop-blur-md font-bold text-xs flex items-center gap-2">
                <Lock className="w-3.5 h-3.5 text-amber-400" />
                <span>{lockedBannerText}</span>
              </div>
            </div>
          )}

          {/* Botão Cadeado Ampliado para Fácil Toque/Clique (1 Toque fica Verde, 2 Toques Destrava) */}
          {isUnlockUIVisible && (
            <div
              className="absolute right-4 sm:right-6 z-50 flex items-center gap-2 sm:gap-3 animate-in fade-in duration-150"
              style={{ top: 'max(1.25rem, env(safe-area-inset-top, 1.25rem))' }}
            >
              {unlockTapCount > 0 ? (
                <span className="px-3 py-1.5 rounded-xl bg-emerald-950/95 text-emerald-300 border border-emerald-400/60 text-xs font-black shadow-2xl backdrop-blur-md animate-pulse">
                  Toque mais 1x para destravar!
                </span>
              ) : (
                <span className="hidden sm:inline-block px-3 py-1.5 rounded-xl bg-black/80 text-amber-300 border border-amber-500/40 text-xs font-bold shadow-lg backdrop-blur-md">
                  Toque 2x para destravar
                </span>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnlockTap(e);
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                }}
                className={`w-11 h-11 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center cursor-pointer transition-all backdrop-blur-xl shadow-2xl ${
                  unlockTapCount > 0
                    ? 'bg-emerald-500 text-black border-4 border-emerald-300 scale-110 shadow-emerald-500/60 ring-4 ring-emerald-400/40 animate-pulse'
                    : 'bg-black/85 hover:bg-black text-amber-300 border-2 border-amber-500/60 shadow-amber-500/30 active:scale-95'
                }`}
                title="Toque 2x no cadeado para destravar"
              >
                {unlockTapCount > 0 ? (
                  <Unlock className="w-5 h-5 sm:w-7 sm:h-7 text-black stroke-[2.5]" />
                ) : (
                  <Lock className="w-5 h-5 sm:w-7 sm:h-7 text-amber-400 stroke-[2.5]" />
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* TEVEZINHA RETRÔ OPEN TV DE CARREGAMENTO */}
      {isBuffering && !hasError && (
        <TvLoadingOverlay
          title={currentChannel.name}
          image={currentChannel.logo}
          type="live"
          status={loadingStatus}
          subtitle={loadingSubtitle}
          bufferingSeconds={bufferingSeconds}
          onPrevChannel={() => {
            zapChannel('prev');
            pokePlayerControls();
          }}
          onNextChannel={() => {
            zapChannel('next');
            pokePlayerControls();
          }}
          onRetry={handleReconnect}
        />
      )}

      {/* Error Overlay with Instant Reconnect */}
      {hasError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 backdrop-blur-md p-6 z-30 text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400 mb-4 shadow-xl">
            <Shield className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">{errorTitle || 'Sinal Temporariamente Indisponível'}</h3>
          <p className="text-sm text-zinc-400 max-w-md mb-2">{errorMessage}</p>
          {errorDetail && (
            <p className="text-[11px] font-mono text-zinc-500 max-w-md mb-6 break-all select-text">Detalhe técnico: {errorDetail}</p>
          )}
          {!errorDetail && <div className="mb-4" />}
          <div className="flex items-center gap-3">
            <button
              onClick={handleReconnect}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-theme-accent hover:opacity-90 text-black font-bold text-sm shadow-lg shadow-theme-accent transition transform active:scale-95 cursor-pointer"
            >
              <RefreshCw className="w-4 h-4" /> Reconectar Sinal
            </button>
            <button
              onClick={stopPlayback}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold text-sm transition cursor-pointer"
            >
              Fechar Player
            </button>
          </div>
        </div>
      )}

      {/* MODAL DE DIAGNÓSTICO TÉCNICO (Permanente conforme AGENTS.md) */}
      {/* Painel de diagnóstico (o mesmo do Stick): rede, latência, memória, armazenamento, capas, vídeo + registros */}
      <DiagnosticsPanel
        open={showDiagnosticModal}
        onClose={() => setShowDiagnosticModal(false)}
        videoElement={videoRef.current}
        logs={diagnosticLogs}
        title="Diagnóstico"
        extraLines={[`Canal: ${currentChannel?.name || '-'}`, `Endereco: ${streamUrl ? streamUrl.split('?')[0].slice(-60) : '-'}`]}
      />

      {/* TOP BAR / OSD */}
      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => {
          lastTouchTimeRef.current = Date.now();
          e.stopPropagation();
        }}
        className={`absolute top-0 left-0 right-0 px-3 sm:px-6 pb-3 sm:pb-5 bg-gradient-to-b from-black/95 via-black/70 to-transparent transition-all duration-300 z-40 flex flex-col gap-1.5 ${
          !isTouchLocked && (isPlayerControlsVisible || isBuffering)
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
        style={{
          paddingTop: 'max(1.5rem, env(safe-area-inset-top, 1.5rem))',
        }}
      >
        {/* ============================================================== */}
        {/* DESKTOP / TV / TABLETS / HORIZONTAL (sm e superior)             */}
        {/* Linha 1: Canal | PROGRAMAÇÃO | Guia TV + CADEADO               */}
        {/* Linha 2: [SD | HD | FHD | 4K] (Centralizado)                   */}
        {/* ============================================================== */}
        <div className="hidden sm:flex flex-col items-center w-full gap-1.5">
          {/* LINHA 1: Canal (esquerda) | Programação (centro) | Guia TV + Cadeado (direita) */}
          <div className="flex items-center justify-between gap-3 md:gap-4 w-full">
            {/* Esquerda: Canal */}
            <div className="flex items-center gap-2.5 shrink-0 min-w-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  stopPlayback();
                }}
                onTouchStart={(e) => e.stopPropagation()}
                className="p-2 sm:p-2.5 rounded-xl bg-white/10 hover:bg-white/20 active:scale-95 text-white backdrop-blur-md transition cursor-pointer shrink-0"
                title="Voltar à grade"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {currentChannel.logo && (
                <img
                  src={getSafeImageUrl(currentChannel.logo)}
                  alt={currentChannel.name}
                  className="w-9 h-9 md:w-10 md:h-10 object-contain rounded-xl bg-black/40 p-1 border border-white/10 shadow-sm shrink-0"
                  onError={(e) => ((e.target as HTMLElement).style.display = 'none')}
                />
              )}

              <div className="min-w-0 max-w-[160px] md:max-w-xs truncate">
                <div className="flex items-center gap-1.5">
                  <span className="text-theme-accent font-mono font-black text-xs sm:text-sm shrink-0">
                    CH {currentChannel.num || '01'}
                  </span>
                  <h2 className="text-white font-bold text-sm sm:text-base md:text-lg truncate">
                    {currentChannel.name}
                  </h2>
                </div>
              </div>
            </div>

            {/* Centro: Programação (espaçosa e sem truncar o título) */}
            <div className="flex flex-1 max-w-md lg:max-w-xl mx-2 md:mx-4 min-w-0 flex-col gap-1 justify-center">
              {currentLiveProgram ? (
                <div className="flex flex-col gap-1 w-full">
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[9px] font-black uppercase tracking-wider shrink-0">
                        No Ar
                      </span>
                      <span className="text-white font-bold truncate text-xs sm:text-sm">
                        {currentLiveProgram.title}
                      </span>
                    </div>
                    <span className="text-[10px] sm:text-xs text-zinc-400 font-mono shrink-0">
                      {new Date(currentLiveProgram.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(currentLiveProgram.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="w-full h-1 sm:h-1.5 rounded-full bg-white/20 overflow-hidden shadow-inner">
                    <div
                      className="h-full bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full transition-all duration-500"
                      style={{ width: `${currentProgramProgress}%` }}
                    />
                  </div>
                </div>
              ) : isEpgLoading ? (
                <div className="flex justify-center items-center gap-1.5 text-[11px] text-amber-400/80 animate-pulse">
                  <Clock className="w-3 h-3 animate-spin" />
                  <span>Carregando guia de programação...</span>
                </div>
              ) : (
                <div className="flex justify-center items-center text-[11px] text-zinc-400">
                  <span>Canal ao Vivo • {currentChannel.groupTitle || 'Geral'}</span>
                </div>
              )}
            </div>

            {/* Direita: Guia TV ao lado do CADEADO */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Guia TV */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEpgDrawer((prev) => !prev);
                }}
                onTouchStart={(e) => e.stopPropagation()}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 active:scale-95 text-white font-semibold text-xs backdrop-blur-md transition cursor-pointer shrink-0"
                title="Abrir Guia EPG (Tecla G)"
              >
                <Calendar className="w-4 h-4 text-theme-accent" />
                <span>Guia TV</span>
              </button>

              {/* Cadeado no Canto */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleLockScreen(e);
                }}
                onTouchStart={(e) => e.stopPropagation()}
                className="p-2 sm:p-2.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/35 border border-amber-500/50 text-amber-300 hover:text-amber-200 backdrop-blur-md transition cursor-pointer shadow-md active:scale-95 flex items-center justify-center min-w-[34px] min-h-[34px] shrink-0"
                title="Bloquear Toques na Tela"
              >
                <Lock className="w-4 h-4 sm:w-4.5 sm:h-4.5" />
              </button>
            </div>
          </div>

          {/* LINHA 2: SD | HD | FHD | 4K (Centralizado) */}
          {availableQualities.length > 0 && (
            <div className="flex items-center justify-center w-full">
              <div className="flex items-center gap-1 bg-black/85 p-0.5 rounded-xl border border-white/25 shadow-lg backdrop-blur-md shrink-0">
                {availableQualities.map((q) => {
                  const isSelected = (currentChannel.currentQuality || currentChannel.resolution || 'SD') === q.resolution;
                  return (
                    <button
                      key={q.resolution}
                      onClick={(e) => {
                        e.stopPropagation();
                        addDiagLog(`[QUALITY] Selecionado ${q.resolution} - streamUrl: ${q.streamUrl}`);
                        switchChannelQuality(q.resolution, q.streamUrl);
                      }}
                      onTouchStart={(e) => e.stopPropagation()}
                      className={`px-2.5 py-1 rounded-lg text-xs font-black font-mono transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-theme-accent text-black font-black shadow-md shadow-theme-accent-sm scale-105 ring-1 ring-white/50'
                          : 'text-zinc-300 hover:text-white hover:bg-white/20 active:scale-95'
                      }`}
                      title={`Alternar transmissão para ${q.resolution}`}
                    >
                      {q.resolution}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ============================================================== */}
        {/* CELULARES / RESPONSIVO NA VERTICAL (sm:hidden):                */}
        {/* Linha 1: na esquerda = logo | canal | na direita = CADEADO     */}
        {/* Linha 2: centralizado = programação                           */}
        {/* Linha 3: centralizado = SD | HD | FHD | 4K | GUIA             */}
        {/* ============================================================== */}
        <div className="flex sm:hidden flex-col w-full gap-2">
          {/* LINHA 1: na esquerda = logo | canal | na direita = CADEADO */}
          <div className="flex items-center justify-between gap-2 w-full">
            {/* Esquerda: Botão Voltar + Logo + Canal */}
            <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  stopPlayback();
                }}
                onTouchStart={(e) => e.stopPropagation()}
                className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 active:scale-95 text-white backdrop-blur-md shrink-0 transition"
                title="Voltar à grade"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {currentChannel.logo && (
                <img
                  src={getSafeImageUrl(currentChannel.logo)}
                  alt={currentChannel.name}
                  className="w-8 h-8 object-contain rounded-lg bg-black/40 p-0.5 border border-white/10 shrink-0"
                  onError={(e) => ((e.target as HTMLElement).style.display = 'none')}
                />
              )}

              <div className="min-w-0 truncate">
                <span className="text-theme-accent font-mono font-black text-[11px] block leading-tight">
                  CH {currentChannel.num || '01'}
                </span>
                <span className="text-white font-bold text-xs block truncate leading-tight">
                  {currentChannel.name}
                </span>
              </div>
            </div>

            {/* Direita: CADEADO */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleLockScreen(e);
              }}
              onTouchStart={(e) => e.stopPropagation()}
              className="p-2 rounded-xl bg-amber-500/20 hover:bg-amber-500/35 border border-amber-500/50 text-amber-300 backdrop-blur-md shadow-sm shrink-0 flex items-center justify-center min-w-[34px] min-h-[34px] active:scale-95 transition"
              title="Bloquear Toques na Tela"
            >
              <Lock className="w-4 h-4" />
            </button>
          </div>

          {/* LINHA 2: centralizado = programação */}
          <div className="flex flex-col items-center justify-center w-full px-1">
            {currentLiveProgram ? (
              <div className="flex flex-col items-center justify-center w-full max-w-sm gap-1">
                <div className="flex items-center justify-between w-full gap-2 text-xs">
                  <div className="flex items-center gap-1.5 min-w-0 truncate">
                    <span className="px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[9px] font-black uppercase shrink-0">
                      No Ar
                    </span>
                    <span className="text-zinc-100 font-bold text-xs truncate">
                      {currentLiveProgram.title}
                    </span>
                  </div>
                  <span className="text-[10px] text-zinc-400 font-mono shrink-0">
                    {new Date(currentLiveProgram.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(currentLiveProgram.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div className="w-full h-1 rounded-full bg-white/20 overflow-hidden shadow-inner">
                  <div
                    className="h-full bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full transition-all duration-500"
                    style={{ width: `${currentProgramProgress}%` }}
                  />
                </div>
              </div>
            ) : isEpgLoading ? (
              <div className="flex justify-center items-center gap-1.5 text-xs text-amber-400/80 animate-pulse py-0.5">
                <Clock className="w-3.5 h-3.5 animate-spin" />
                <span>Carregando guia de programação...</span>
              </div>
            ) : (
              <div className="flex justify-center items-center text-xs text-zinc-400 py-0.5">
                <span>Canal ao Vivo • {currentChannel.groupTitle || 'Geral'}</span>
              </div>
            )}
          </div>

          {/* LINHA 3: centralizado = SD | HD | FHD | 4K | GUIA */}
          <div className="flex items-center justify-center gap-1.5 w-full">
            <div className="flex items-center gap-0.5 bg-black/85 p-0.5 rounded-xl border border-white/20 shadow-md backdrop-blur-md">
              {availableQualities.map((q) => {
                const isSelected = (currentChannel.currentQuality || currentChannel.resolution || 'SD') === q.resolution;
                return (
                  <button
                    key={q.resolution}
                    onClick={(e) => {
                      e.stopPropagation();
                      addDiagLog(`[QUALITY] Selecionado ${q.resolution} - streamUrl: ${q.streamUrl}`);
                      switchChannelQuality(q.resolution, q.streamUrl);
                    }}
                    onTouchStart={(e) => e.stopPropagation()}
                    className={`px-2.5 py-1 rounded-lg text-xs font-black font-mono transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-theme-accent text-black font-black shadow-sm scale-105 ring-1 ring-white/50'
                        : 'text-zinc-300 hover:text-white hover:bg-white/20 active:scale-95'
                    }`}
                    title={`Alternar transmissão para ${q.resolution}`}
                  >
                    {q.resolution}
                  </button>
                );
              })}

              {availableQualities.length > 0 && (
                <div className="w-[1px] h-3.5 bg-white/20 mx-0.5 shrink-0" />
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEpgDrawer((prev) => !prev);
                }}
                onTouchStart={(e) => e.stopPropagation()}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold text-white hover:text-white hover:bg-white/20 active:scale-95 transition cursor-pointer shrink-0"
                title="Abrir Guia EPG"
              >
                <Calendar className="w-3.5 h-3.5 text-theme-accent" />
                <span>GUIA</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM CONTROLS / OSD */}
      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => {
          lastTouchTimeRef.current = Date.now();
          e.stopPropagation();
        }}
        className={`absolute bottom-0 left-0 right-0 px-2.5 sm:px-4 md:px-6 pt-3 sm:pt-4 bg-gradient-to-t from-black/95 via-black/70 to-transparent transition-all duration-300 z-40 ${
          !isTouchLocked && (isPlayerControlsVisible || isBuffering)
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        style={{
          paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0.75rem))',
        }}
      >
        <div className="flex items-center justify-between gap-1 sm:gap-3 max-w-full overflow-x-auto scrollbar-none">
          {/* Left: Prev Zap, Play/Pause, Next Zap, Mute, Reconnect */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                zapChannel('prev');
                pokePlayerControls();
              }}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-9 h-9 sm:w-10 sm:h-10 p-1.5 sm:p-2.5 rounded-xl sm:rounded-xl bg-zinc-800/90 hover:bg-zinc-700 active:scale-95 text-zinc-200 hover:text-white transition cursor-pointer flex items-center justify-center shrink-0"
              title="Canal Anterior (Seta Cima / [)"
            >
              <ChevronLeft className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                togglePlay();
                pokePlayerControls();
              }}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-11 h-11 sm:w-12 sm:h-12 p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-theme-accent hover:opacity-90 text-black shadow-lg shadow-theme-accent transition transform active:scale-95 cursor-pointer flex items-center justify-center shrink-0"
              title={isPlaying ? 'Pausar' : 'Reproduzir'}
            >
              {isPlaying ? <Pause className="w-5 h-5 sm:w-5 sm:h-5 fill-black text-black" /> : <Play className="w-5 h-5 sm:w-5 sm:h-5 fill-black text-black ml-0.5" />}
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                zapChannel('next');
                pokePlayerControls();
              }}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-9 h-9 sm:w-10 sm:h-10 p-1.5 sm:p-2.5 rounded-xl sm:rounded-xl bg-zinc-800/90 hover:bg-zinc-700 active:scale-95 text-zinc-200 hover:text-white transition cursor-pointer flex items-center justify-center shrink-0"
              title="Próximo Canal (Seta Baixo / ])"
            >
              <ChevronRight className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleMute();
              }}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-7 h-7 sm:w-10 sm:h-10 p-1 sm:p-2.5 rounded-lg sm:rounded-xl bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200 hover:text-white transition cursor-pointer flex items-center justify-center shrink-0"
              title="Silenciar / Ativar Áudio (M)"
            >
              {isMuted ? <VolumeX className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-red-400" /> : <Volume2 className="w-3.5 h-3.5 sm:w-5 sm:h-5" />}
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleReconnect();
              }}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-7 h-7 sm:w-10 sm:h-10 p-1 sm:p-2.5 rounded-lg sm:rounded-xl bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200 hover:text-white transition cursor-pointer flex items-center justify-center shrink-0"
              title="Recarregar / Destravar sinal ao vivo"
            >
              <RefreshCw className={`w-3.5 h-3.5 sm:w-5 sm:h-5 ${isManualReconnecting ? 'animate-spin text-theme-accent' : ''}`} />
            </button>
          </div>

          {/* Right: Audio, PiP, Cast, Fullscreen */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Audio Selector */}
            {audioTracks.length > 1 && (
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAudioMenu((prev) => !prev);
                  }}
                  onTouchStart={(e) => e.stopPropagation()}
                  className="w-7 h-7 sm:w-10 sm:h-10 p-1 sm:p-2.5 rounded-lg sm:rounded-xl bg-zinc-800/90 hover:bg-zinc-700 text-zinc-300 hover:text-white transition cursor-pointer flex items-center justify-center shrink-0"
                  title="Selecionar Áudio"
                >
                  <Layers className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
                </button>

                {showAudioMenu && (
                  <div className="absolute bottom-12 right-0 w-44 rounded-xl bg-zinc-900 border border-zinc-700 p-2 shadow-2xl z-40 space-y-1">
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider px-2 py-1 block">
                      Faixas de Áudio
                    </span>
                    {audioTracks.map((tr) => (
                      <button
                        key={tr.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (hlsRef.current) hlsRef.current.audioTrack = tr.id;
                          setCurrentAudioTrack(tr.id);
                          setShowAudioMenu(false);
                          showToast(`Áudio: ${tr.name}`, 'info');
                        }}
                        onTouchStart={(e) => e.stopPropagation()}
                        className={`w-full text-left text-xs px-2.5 py-1.5 rounded-lg transition font-medium cursor-pointer ${
                          currentAudioTrack === tr.id
                            ? 'bg-theme-accent text-black font-bold'
                            : 'text-zinc-300 hover:bg-zinc-800'
                        }`}
                      >
                        {tr.name} {tr.lang ? `(${tr.lang})` : ''}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Diagnóstico Técnico / Telemetria — V8.5.1: só a conta MARIOZELI (admin/desenvolvedor) */}
            {canSeeTelemetry && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDiagnosticModal(true);
              }}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-7 h-7 sm:w-10 sm:h-10 p-1 sm:p-2.5 rounded-lg sm:rounded-xl bg-emerald-500/20 hover:bg-emerald-500/35 border border-emerald-500/40 text-emerald-300 transition cursor-pointer flex items-center justify-center shrink-0"
              title="Diagnóstico HLS / Transmissão"
            >
              <Activity className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
            </button>
            )}

            {/* Picture in Picture (oculto no modo instalado do iOS, onde o WebKit não permite PiP) */}
            {!isStandaloneIOS && (typeof document !== 'undefined' && (
              document.pictureInPictureEnabled ||
              (typeof window !== 'undefined' && !!(window as any).AndroidBridge?.enterPictureInPicture) ||
              (typeof HTMLVideoElement !== 'undefined' && (
                'webkitSetPresentationMode' in HTMLVideoElement.prototype ||
                'webkitSupportsPresentationMode' in HTMLVideoElement.prototype
              )) ||
              (videoRef.current && (videoRef.current as any).webkitSupportsPresentationMode?.('picture-in-picture')) ||
              (typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent))
            )) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  togglePiP();
                }}
                onTouchStart={(e) => e.stopPropagation()}
                className={`w-7 h-7 sm:w-10 sm:h-10 p-1 sm:p-2.5 rounded-lg sm:rounded-xl transition cursor-pointer flex items-center justify-center shrink-0 ${
                  isPipActive
                    ? 'bg-theme-accent text-white shadow-lg ring-2 ring-white/50'
                    : 'bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200 hover:text-white'
                }`}
                title={isPipActive ? "Sair da Janela Flutuante (PiP)" : "Picture-in-Picture (Janela Flutuante)"}
              >
                <SquareDashedBottom className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
              </button>
            )}

            {/* Compartilhar tela / Cast */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCast();
              }}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-7 h-7 sm:w-10 sm:h-10 p-1 sm:p-2.5 rounded-lg sm:rounded-xl bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200 hover:text-white transition cursor-pointer flex items-center justify-center shrink-0"
              title="Transmitir na Smart TV (Open TV Connection)"
            >
              <Cast className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
            </button>

            {/* Fullscreen / Maximizar (Ativa player nativo no iOS e previne bloqueio de tela) */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFullscreen();
              }}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-7 h-7 sm:w-10 sm:h-10 p-1 sm:p-2.5 rounded-lg sm:rounded-xl bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200 hover:text-white transition cursor-pointer flex items-center justify-center shrink-0"
              title="Tela Cheia (F)"
            >
              {isFullscreen ? <Minimize2 className="w-3.5 h-3.5 sm:w-5 sm:h-5" /> : <Maximize2 className="w-3.5 h-3.5 sm:w-5 sm:h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* EPG DRAWER OVERLAY */}
      {showEpgDrawer && currentChannel && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex justify-end"
          onClick={() => setShowEpgDrawer(false)}
        >
          <div
            ref={epgDrawerRef}
            onClick={(e) => e.stopPropagation()}
            className="w-80 md:w-96 h-full bg-zinc-950/95 border-l border-zinc-800 p-4 flex flex-col backdrop-blur-2xl shadow-2xl"
          >
            <div className="flex items-center justify-between pb-3 border-b border-zinc-800 mb-3 shrink-0">
              <h4 className="text-sm font-bold text-white flex items-center gap-2">
                <Calendar className="w-4 h-4 text-amber-400" /> Guia • {currentChannel.name}
              </h4>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => loadChannelEPG(currentChannel, true)}
                  className="p-1.5 rounded-xl text-zinc-400 hover:text-white bg-zinc-900 hover:bg-zinc-800 transition cursor-pointer"
                  title="Atualizar Guia"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isEpgLoading ? 'animate-spin text-amber-400' : ''}`} />
                </button>
                <button
                  onClick={() => setShowEpgDrawer(false)}
                  className="p-1.5 rounded-xl text-zinc-400 hover:text-white bg-zinc-900 hover:bg-zinc-800 transition cursor-pointer"
                  title="Fechar Guia"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div
              className="flex-1 overflow-y-auto space-y-2.5 pr-1 scrollbar-thin scrollbar-thumb-zinc-800 overscroll-contain touch-pan-y"
              style={{ overscrollBehaviorY: 'contain' }}
            >
              {isEpgLoading && rawChannelEpgPrograms.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center text-zinc-400">
                  <RefreshCw className="w-6 h-6 text-amber-400 animate-spin mb-3" />
                  <p className="text-xs font-bold text-white">Carregando programação oficial...</p>
                  <span className="text-[10px] text-zinc-500 mt-1">Conectando ao servidor Fusion</span>
                </div>
              ) : rawChannelEpgPrograms.length === 0 ? (
                <div className="text-center py-16 px-4 text-zinc-500 text-xs font-bold uppercase tracking-wider flex flex-col items-center">
                  <Calendar className="w-8 h-8 text-zinc-700 mb-2" />
                  <span>Sem programação disponível para este canal</span>
                  <button
                    onClick={() => loadChannelEPG(currentChannel, true)}
                    className="mt-3 px-3 py-1.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-amber-400 text-[11px] font-bold border border-zinc-800 cursor-pointer transition"
                  >
                    Tentar Novamente
                  </button>
                </div>
              ) : (
                <>
                  {/* Programação Atual (No Ar Agora) e Próximas 24 Horas */}
                  {channelEpgPrograms.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500 text-xs">
                      A programação deste bloco encerrou. Aguardando atualização da grade.
                    </div>
                  ) : (
                    channelEpgPrograms.map((prog, idx) => {
                      const now = new Date();
                      const start = new Date(prog.startTime);
                      const end = new Date(prog.endTime);
                      const isLiveNow = (now >= start && now <= end) || (currentLiveProgram?.id === prog.id);
                      const isNextUp = !isLiveNow && idx === (channelEpgPrograms[0]?.id === currentLiveProgram?.id ? 1 : 0);

                      return (
                        <div
                          key={prog.id || idx}
                          className={`p-3 rounded-xl border transition-all text-left ${
                            isLiveNow
                              ? 'bg-amber-950/50 border-amber-500/70 shadow-lg ring-1 ring-amber-500/40'
                              : isNextUp
                              ? 'bg-theme-accent-subtle border-theme-accent-subtle'
                              : 'bg-zinc-900/60 border-zinc-800/80'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <span
                              className={`text-[10px] font-mono font-bold flex items-center gap-1 ${
                                isLiveNow ? 'text-amber-400' : isNextUp ? 'text-theme-accent' : 'text-zinc-400'
                              }`}
                            >
                              <Clock className="w-3 h-3" />
                              {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} -{' '}
                              {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <div className="flex items-center gap-1.5">
                              {isLiveNow ? (
                                <span className="text-[9px] font-black px-2 py-0.5 rounded bg-gradient-to-r from-amber-500 to-yellow-400 text-black uppercase tracking-wider shadow-sm">
                                  No Ar Agora
                                </span>
                              ) : isNextUp ? (
                                <span className="text-[9px] font-black px-2 py-0.5 rounded bg-theme-accent-subtle text-theme-accent border border-theme-accent-subtle uppercase tracking-wider">
                                  A Seguir
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <h5 className="text-xs font-bold text-white mb-1 leading-snug">{prog.title}</h5>
                          {isLiveNow && (
                            <div className="w-full bg-black/40 rounded-full h-1 overflow-hidden my-1.5">
                              <div
                                className="bg-amber-400 h-full rounded-full transition-all duration-300"
                                style={{ width: `${currentProgramProgress}%` }}
                              />
                            </div>
                          )}
                          {prog.description && (
                            <p className="text-[11px] text-zinc-400 leading-relaxed line-clamp-2">{prog.description}</p>
                          )}
                        </div>
                      );
                    })
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cast Modal (Chromecast & AirPlay) */}
      {isCastModalOpen && (
        <CastModal
          isOpen={isCastModalOpen}
          onClose={() => setIsCastModalOpen(false)}
          videoElement={videoRef.current}
          streamTitle={currentChannel.name}
          directStreamUrl={currentChannel.streamUrl}
          proxiedStreamUrl={streamUrl}
        />
      )}
    </div>
  );
};
